import React, { useState, useEffect, useCallback, useMemo, useRef, useContext } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  FaUserPlus,
  FaSearch,
  FaUserCheck,
  FaTimes,
  FaUserFriends,
  FaEnvelope,
  FaPhone,
  FaCalendarAlt,
  FaSort,
  FaStar,
  FaRegStar,
  FaRegEye,
  FaRegEyeSlash,
  FaRupeeSign,
  FaFileImport,
  FaFilter,
  FaChevronDown,
  FaChevronUp,
  FaInfoCircle,
  FaTrash,
  FaEllipsisV,
  FaCheckCircle,
  FaMoneyBillWave,
  FaWallet,
  FaPercent,
  FaGraduationCap,
  FaCreditCard,
  FaHistory,
  FaExclamationTriangle,
  FaClock,
  FaBook,
} from 'react-icons/fa';
import HttpInterceptor from '../../lib/HttpInterceptor';
import { toast } from 'react-toastify';
import StarRating from '../../Components/StarRating';
import Avatar from '../../Components/Avatar';
import SkeletonLoader from '../../Components/SkeletonLoader';
import PaymentHistoryModal from '../../Components/PaymentHistoryModal';
import PaymentPlanAssignmentModal from '../../Components/PaymentPlanAssignmentModal';
import ProgressBreakdownModal from '../../Components/ProgressBreakdownModal';
import AttendanceCalendarModal from '../../Components/AttendanceCalendarModal';
import CommunicationHistoryModal from '../../Components/CommunicationHistoryModal';
import { transformStudentData, transformProgramData, formatName } from '../../utils/dataTransformers';
import { DEBOUNCE_DELAY } from '../../constants/appConstants';
import { formatDate as formatDateUtil } from '../../utils/dateFormatter';
import { formatCurrency } from '../../utils/currencyFormatter';
import { formatPaymentStatus, getPaymentStatusColor } from '../../utils/paymentStatusFormatter';
import { DATE_FORMATS } from '../../constants/appConstants';
import UserContext from '../../Components/UserContext';
import { getUserFriendlyError, getContextualError } from '../../utils/errorMessages';

function MyStudents() {
  const { session } = useContext(UserContext);
  const [searchParams, setSearchParams] = useSearchParams();

  // Get service context
  const trainerServices = (session?.services && session.services.length > 0) ? session.services : (session?.service ? [session.service] : []);
  const servicePartnerTypes = session?.servicePartnerTypes || {};
  const activeService = localStorage.getItem('activeService') || trainerServices[0] || '';
  const activePartnerType = activeService ? (servicePartnerTypes[activeService] || 'instructor') : 'instructor';
  const isConsultant = activePartnerType === 'consultant';

  const [students, setStudents] = useState([]);
  const [appointments, setAppointments] = useState([]); // For consultants
  const [isPWA, setIsPWA] = useState(false);
  const [programs, setPrograms] = useState([]);
  const [paymentPlans, setPaymentPlans] = useState([]);
  const [trainerSubscriptionPlan, setTrainerSubscriptionPlan] = useState('free');
  const [advanceFeatures, setAdvanceFeatures] = useState({ showHideTestimonials: false });
  const [searchTerm, setSearchTerm] = useState(searchParams.get('search') || '');
  const [activeTab, setActiveTab] = useState('all');
  const [sortConfig, setSortConfig] = useState(() => {
    const sortKey = searchParams.get('sortKey') || 'name';
    const sortDir = searchParams.get('sortDir') || 'ascending';
    return { key: sortKey, direction: sortDir };
  });
  const [showAddModal, setShowAddModal] = useState(false);
  const [showBulkImportModal, setShowBulkImportModal] = useState(false);
  const [newStudent, setNewStudent] = useState({
    name: '',
    email: '',
    phone: '',
    gender: 'other',
    program: '',
    programDetails: { name: '', price: 0, duration: '' },
  });
  const [csvData, setCsvData] = useState('');
  const [error, setError] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState(() => ({
    program: searchParams.get('program') || '',
    status: searchParams.get('status') || '',
    minProgress: searchParams.get('minProgress') || '',
    maxProgress: searchParams.get('maxProgress') || '',
    hasReview: searchParams.get('hasReview') || '',
  }));
  // Confirmation modal states
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteItem, setDeleteItem] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isAddingStudent, setIsAddingStudent] = useState(false);
  const [showAttendanceModal, setShowAttendanceModal] = useState(null);
  const [classAttendance, setClassAttendance] = useState([]);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteData, setInviteData] = useState({
    contact: '',
    contactType: 'email', // 'email' or 'whatsapp'
    programId: '',
    message: ''
  });
  const [isInviting, setIsInviting] = useState(false);
  const [dateFormat, setDateFormat] = useState(DATE_FORMATS.DD_MM_YYYY); // Default date format
  const [showMessageModal, setShowMessageModal] = useState(false);
  const [messageStudentId, setMessageStudentId] = useState(null);
  const [messageContent, setMessageContent] = useState('');
  const [isSendingMessage, setIsSendingMessage] = useState(false);
  const [openActionMenu, setOpenActionMenu] = useState(null);
  const [showPaymentHistory, setShowPaymentHistory] = useState(null);
  const [sendingReminder, setSendingReminder] = useState(null);
  const [showPlanAssignment, setShowPlanAssignment] = useState(null);
  const [showProgressBreakdown, setShowProgressBreakdown] = useState(null);
  const [showAttendanceCalendar, setShowAttendanceCalendar] = useState(null);
  const [showCommunicationHistory, setShowCommunicationHistory] = useState(null);
  const actionMenuRefs = useRef({});
  const actionMenuFocusedIndex = useRef({});
  const screenReaderAnnouncement = useRef(null);

  // Keyboard navigation handler for action menus
  const handleActionMenuKeyDown = useCallback((e, studentId, menuItems) => {
    if (!openActionMenu || openActionMenu !== studentId) return;

    const currentIndex = actionMenuFocusedIndex.current[studentId] || 0;
    let newIndex = currentIndex;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        newIndex = Math.min(currentIndex + 1, menuItems.length - 1);
        actionMenuFocusedIndex.current[studentId] = newIndex;
        if (actionMenuRefs.current[`${studentId}-${newIndex}`]) {
          actionMenuRefs.current[`${studentId}-${newIndex}`].focus();
        }
        break;
      case 'ArrowUp':
        e.preventDefault();
        newIndex = Math.max(currentIndex - 1, 0);
        actionMenuFocusedIndex.current[studentId] = newIndex;
        if (actionMenuRefs.current[`${studentId}-${newIndex}`]) {
          actionMenuRefs.current[`${studentId}-${newIndex}`].focus();
        }
        break;
      case 'Escape':
        e.preventDefault();
        setOpenActionMenu(null);
        actionMenuFocusedIndex.current[studentId] = 0;
        break;
      case 'Home':
        e.preventDefault();
        newIndex = 0;
        actionMenuFocusedIndex.current[studentId] = newIndex;
        if (actionMenuRefs.current[`${studentId}-${newIndex}`]) {
          actionMenuRefs.current[`${studentId}-${newIndex}`].focus();
        }
        break;
      case 'End':
        e.preventDefault();
        newIndex = menuItems.length - 1;
        actionMenuFocusedIndex.current[studentId] = newIndex;
        if (actionMenuRefs.current[`${studentId}-${newIndex}`]) {
          actionMenuRefs.current[`${studentId}-${newIndex}`].focus();
        }
        break;
    }
  }, [openActionMenu]);

  // Screen reader announcement helper
  const announceToScreenReader = useCallback((message) => {
    if (screenReaderAnnouncement.current) {
      screenReaderAnnouncement.current.textContent = message;
      setTimeout(() => {
        if (screenReaderAnnouncement.current) {
          screenReaderAnnouncement.current.textContent = '';
        }
      }, 1000);
    }
  }, []);


  // Keyboard shortcut for search (Ctrl+K or Cmd+K)
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault();
        const searchInput = document.querySelector(isConsultant ? '[aria-label="Search clients by name, email, or phone"]' : '[aria-label="Search students by name, email, or phone"]');
        if (searchInput) {
          searchInput.focus();
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);
  const [currentPage, setCurrentPage] = useState(() => {
    const page = parseInt(searchParams.get('page') || '1', 10);
    return page > 0 ? page : 1;
  });
  const [itemsPerPage, setItemsPerPage] = useState(() => {
    const items = searchParams.get('itemsPerPage') || '12';
    if (items === 'all') return 'all';
    const itemsNum = parseInt(items, 10);
    return [12, 24, 48].includes(itemsNum) ? itemsNum : 12;
  });

  // Detect PWA mode
  useEffect(() => {
    const checkPWA = () => {
      const standalone = window.matchMedia('(display-mode: standalone)').matches ||
        window.navigator.standalone === true;
      setIsPWA(standalone);
    };
    checkPWA();
    const mediaQuery = window.matchMedia('(display-mode: standalone)');
    const handleChange = () => checkPWA();
    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  // Fetch date format settings and trainer subscription plan
  useEffect(() => {
    const fetchDateFormat = async () => {
      try {
        const response = await HttpInterceptor.get('/api/trainer-settings', {
          showErrorToast: false // Suppress error toasts
        });
        if (response.data?.data?.dateFormat) {
          setDateFormat(response.data.data.dateFormat);
        }
      } catch (err) {
        // Silently fail - use default format
        console.log('Could not fetch date format settings, using default');
      }
    };

    const fetchTrainerProfile = async () => {
      try {
        const response = await HttpInterceptor.get('/api/trainer/profile', {
          showErrorToast: false
        });
        const profileData = response.data?.data || response.data || {};
        if (profileData.subscriptionPlan) {
          setTrainerSubscriptionPlan(profileData.subscriptionPlan);
        }
        if (profileData.advanceFeatures) {
          setAdvanceFeatures(profileData.advanceFeatures);
        }
      } catch (err) {
        // Silently fail - use default 'free' plan
        console.log('Could not fetch trainer profile, using default subscription plan');
      }
    };

    const fetchPaymentPlans = async () => {
      try {
        const response = await HttpInterceptor.get('/api/fee-plan/installments', {
          showErrorToast: false
        });
        const plansData = Array.isArray(response.data) ? response.data : (response.data?.data || []);
        setPaymentPlans(plansData);
      } catch (err) {
        console.log('Could not fetch payment plans, using empty array');
        setPaymentPlans([]);
      }
    };

    fetchDateFormat();
    fetchTrainerProfile();
    fetchPaymentPlans();
  }, []);

  // API Functions
  const fetchPrograms = useCallback(async () => {
    try {
      const { data } = await HttpInterceptor.get('/api/programs');
      const programsArray = transformProgramData(data);
      setPrograms(programsArray);
    } catch (err) {
      // Use user-friendly error messages
      const errorMsg = getContextualError('fetch programs', err);
      setError(errorMsg);
      console.error('Fetch programs error:', err);
      // Only show toast if it's a server error (HttpInterceptor handles network errors)
      if (err.type !== 'network' && err.status !== 401) {
        toast.error(errorMsg);
      }
      setPrograms([]); // Set empty array on error
    }
  }, []);

  const getStudents = useCallback(async (customFilters = {}) => {
    try {
      setIsLoading(true);
      // Combine component filters with any passed custom filters
      const allFilters = { ...filters, ...customFilters };

      // Remove empty filters
      const cleanFilters = Object.fromEntries(
        Object.entries(allFilters).filter(([_, v]) => v !== '' && v !== null)
      );

      // Fetch programs if not already loaded (for price lookup)
      let programsForMap = programs;
      if (!programsForMap || programsForMap.length === 0) {
        try {
          const { data: programsData } = await HttpInterceptor.get('/api/programs');
          programsForMap = transformProgramData(programsData);
        } catch (err) {
          console.warn('Failed to fetch programs for price lookup:', err);
          programsForMap = [];
        }
      }

      const { data } = await HttpInterceptor.get('/api/students', {
        params: cleanFilters
      });

      // Create a map of programs by ID for price lookup
      // This helps get the current program price if student's finalPrice is 0
      const programsMap = new Map();
      if (programsForMap && programsForMap.length > 0) {
        programsForMap.forEach(program => {
          if (program._id) {
            const programId = program._id.toString();
            programsMap.set(programId, program);
            console.log(`Added program to map: ${program.name} (ID: ${programId}, currentPrice: ${program.currentPrice}, basePrice: ${program.basePrice}, price: ${program.price})`);
          }
        });
        console.log(`ProgramsMap size: ${programsMap.size}`);
      } else {
        console.warn('No programs found for price lookup');
      }

      const transformedStudents = transformStudentData(data, programsMap);
      setStudents(transformedStudents);
      setIsLoading(false);
    } catch (err) {
      console.error('Fetch students error:', err);
      setIsLoading(false);

      // Use user-friendly error messages
      const errorMsg = getContextualError('fetch students', err);
      setError(errorMsg);
      setStudents([]);
      // Only show toast if it's a server error (HttpInterceptor handles network errors)
      if (err.type !== 'network' && err.status !== 401) {
        toast.error(errorMsg);
      }
    }
  }, [filters]);

  const addStudent = async (studentData) => {
    try {
      const { data } = await HttpInterceptor.post('/api/students', studentData);

      // Transform the backend student data to match frontend format
      const transformedStudent = {
        planId: data.student.planId,
        studentId: data.student.user,
        name: data.student.personalDetails.name,
        email: data.student.personalDetails.email,
        phone: data.student.personalDetails.phone,
        status: data.student.enrollment.status,
        progress: data.student.attendance.attendancePercentage,
        lastSession: data.student.attendance.lastSession,
        image: data.student.image,
        programDetails: {
          name: data.student.program.name,
          price: data.student.program.finalPrice,
          duration: data.student.program.duration
        },
        paymentStatus: data.student.payment.status,
        review: data.student.review,
        joinDate: data.student.enrollment.date,
        isActive: data.student.isActive
      };

      setStudents((prev) => [...prev, transformedStudent]);
      const studentName = data.student?.personalDetails?.name || data.student?.name || 'Student';
      toast.success(`✓ ${studentName} has been added successfully`);
      return transformedStudent;
    } catch (err) {
      const errorMsg = getContextualError('add student', err);
      setError(errorMsg);
      console.error('Add student error:', err);
      toast.error(errorMsg);
      throw err;
    }
  };

  const bulkImportStudents = async (studentsData) => {
    try {
      // Note: Bulk import endpoint not implemented in backend
      // Import students individually for now
      const results = [];
      for (const studentData of studentsData) {
        try {
          const student = await addStudent(studentData);
          results.push(student);
        } catch (err) {
          console.error('Error importing student:', err);
          // Continue with next student
        }
      }
      toast.success(`✓ ${results.length} of ${studentsData.length} students imported successfully`);
      return results;
    } catch (err) {
      const errorMsg = err.response?.data?.message || err.message || 'Failed to import students';
      setError(errorMsg);
      console.error('Bulk import error:', err);
      toast.error(errorMsg);
      throw err;
    }
  };

  const updateStudentStatus = async (planId, updates) => {
    try {
      // Sanitize planId to prevent path traversal attacks
      if (!planId || typeof planId !== 'string') {
        throw new Error('Invalid plan ID');
      }

      // Only allow alphanumeric, hyphens, and underscores (MongoDB ObjectId format)
      const sanitizedPlanId = planId.replace(/[^a-zA-Z0-9\-_]/g, '');
      if (sanitizedPlanId !== planId || sanitizedPlanId.length === 0) {
        throw new Error('Invalid plan ID format');
      }

      const { data } = await HttpInterceptor.patch(`/api/students/status/${sanitizedPlanId}`, updates);

      // Get the current student name before refreshing
      const currentStudent = students.find(s => s.planId === planId);
      const studentName = currentStudent?.name || data.student?.personalDetails?.name || data.student?.name || 'Student';

      // Check notification status and show appropriate messages
      const notificationsFailed = [];
      if (data.emailSent === false) {
        notificationsFailed.push('Email');
      }
      if (data.whatsappSent === false && data.whatsappError) {
        notificationsFailed.push('WhatsApp');
      }

      if (notificationsFailed.length > 0) {
        // Show warning toast with notification failure information
        const failedMethods = notificationsFailed.join(' and ');
        toast.warning(
          <div>
            <div style={{ fontWeight: 'bold', marginBottom: '8px' }}>
              ⚠️ Approval {failedMethods} {notificationsFailed.length === 1 ? 'Failed' : 'Failed'}
            </div>
            {data.emailError && (
              <div style={{ fontSize: '14px', marginBottom: '4px' }}>
                <strong>Email:</strong> {data.emailError}
              </div>
            )}
            {data.whatsappError && (
              <div style={{ fontSize: '14px', marginBottom: '4px' }}>
                <strong>WhatsApp:</strong> {data.whatsappError}
              </div>
            )}
            {data.warning && (
              <div style={{ fontSize: '13px', color: '#6b7280', marginTop: '8px' }}>
                {data.warning}
              </div>
            )}
          </div>,
          {
            autoClose: 12000, // Show for 12 seconds
            style: { maxWidth: '500px' }
          }
        );
        announceToScreenReader(`Student ${studentName} has been approved, but ${failedMethods.toLowerCase()} notification${notificationsFailed.length > 1 ? 's' : ''} could not be sent. Please contact the student personally.`);
      } else {
        const sentMethods = [];
        if (data.emailSent) sentMethods.push('Email');
        if (data.whatsappSent) sentMethods.push('WhatsApp');
        const successMessage = sentMethods.length > 0
          ? `✓ ${studentName} has been approved successfully. Notification sent via ${sentMethods.join(' and ')}.`
          : `✓ ${studentName} has been approved successfully`;
        toast.success(successMessage);
        announceToScreenReader(`Student ${studentName} has been approved`);
      }

      // Refresh the full student list to ensure all data is up to date and properly transformed
      // This ensures the student data matches the expected frontend format
      await getStudents();

      return data.student;
    } catch (err) {
      const errorMsg = getContextualError('update status', err);
      setError(errorMsg);
      console.error('Update status error:', err);
      toast.error(errorMsg);
      throw err;
    }
  };

  const updateStudentProgress = async (planId, progressData) => {
    try {
      // Sanitize planId to prevent path traversal attacks
      if (!planId || typeof planId !== 'string') {
        throw new Error('Invalid plan ID');
      }

      // Only allow alphanumeric, hyphens, and underscores (MongoDB ObjectId format)
      const sanitizedPlanId = planId.replace(/[^a-zA-Z0-9\-_]/g, '');
      if (sanitizedPlanId !== planId || sanitizedPlanId.length === 0) {
        throw new Error('Invalid plan ID format');
      }

      const { data } = await HttpInterceptor.patch(`/api/students/progress/${sanitizedPlanId}`, progressData);
      setStudents((prev) =>
        prev.map((s) => (s.planId === planId ? data.student : s))
      );
      const student = students.find(s => s.planId === planId);
      const studentName = student?.name || data.student?.personalDetails?.name || data.student?.name || 'Student';
      toast.success(`✓ Progress updated for ${studentName}`);
      return data.student;
    } catch (err) {
      const errorMsg = getContextualError('update progress', err);
      setError(errorMsg);
      console.error('Update progress error:', err);
      toast.error(errorMsg);
      throw err;
    }
  };

  const updateStudentReview = async (planId, reviewData) => {
    try {
      // Sanitize planId to prevent path traversal attacks
      if (!planId || typeof planId !== 'string') {
        throw new Error('Invalid plan ID');
      }

      // Only allow alphanumeric, hyphens, and underscores (MongoDB ObjectId format)
      const sanitizedPlanId = planId.replace(/[^a-zA-Z0-9\-_]/g, '');
      if (sanitizedPlanId !== planId || sanitizedPlanId.length === 0) {
        throw new Error('Invalid plan ID format');
      }

      const { data } = await HttpInterceptor.patch(`/api/students/review/${sanitizedPlanId}`, reviewData);

      // Update only the review property to preserve the transformed student structure
      setStudents((prev) =>
        prev.map((s) => {
          if (s.planId === planId) {
            // Preserve all existing student data, only update the review
            return {
              ...s,
              review: data.student?.review || s.review
            };
          }
          return s;
        })
      );
      const student = students.find(s => s.planId === planId);
      const studentName = student?.name || data.student?.personalDetails?.name || data.student?.name || 'Student';
      toast.success(`✓ Review updated for ${studentName}`);
      return data.student;
    } catch (err) {
      const errorMsg = getContextualError('update review', err);
      setError(errorMsg);
      console.error('Update review error:', err);
      toast.error(errorMsg);
      throw err;
    }
  };

  const removeStudent = async (planId) => {
    try {
      // Sanitize planId to prevent path traversal attacks
      if (!planId || typeof planId !== 'string') {
        throw new Error('Invalid plan ID');
      }

      // Only allow alphanumeric, hyphens, and underscores (MongoDB ObjectId format)
      const sanitizedPlanId = planId.replace(/[^a-zA-Z0-9\-_]/g, '');
      if (sanitizedPlanId !== planId || sanitizedPlanId.length === 0) {
        throw new Error('Invalid plan ID format');
      }

      await HttpInterceptor.delete(`/api/students/${sanitizedPlanId}`);
      setStudents((prev) => prev.filter((s) => s.planId !== planId));
      toast.success('Student removed successfully');
      return { success: true };
    } catch (err) {
      const errorMsg = err.response?.data?.message || err.message || 'Failed to delete student';
      setError(errorMsg);
      console.error('Delete student error:', err);
      toast.error(errorMsg);
      throw err;
    }
  };

  // Sync URL params with state changes
  useEffect(() => {
    const params = new URLSearchParams();
    if (searchTerm) params.set('search', searchTerm);
    if (filters.program) params.set('program', filters.program);
    if (filters.status) params.set('status', filters.status);
    if (filters.minProgress) params.set('minProgress', filters.minProgress);
    if (filters.maxProgress) params.set('maxProgress', filters.maxProgress);
    if (filters.hasReview) params.set('hasReview', filters.hasReview);
    if (sortConfig.key !== 'name' || sortConfig.direction !== 'ascending') {
      params.set('sortKey', sortConfig.key);
      params.set('sortDir', sortConfig.direction);
    }
    if (currentPage > 1) params.set('page', currentPage.toString());
    if (itemsPerPage !== 12) {
      if (itemsPerPage === 'all') {
        params.set('itemsPerPage', 'all');
      } else {
        params.set('itemsPerPage', itemsPerPage.toString());
      }
    }
    setSearchParams(params, { replace: true });
  }, [searchTerm, filters, sortConfig, currentPage, itemsPerPage, setSearchParams]);

  // Fetch appointments for consultants
  const fetchAppointments = useCallback(async () => {
    try {
      setIsLoading(true);
      const params = new URLSearchParams();
      if (activeService) {
        params.append('service', activeService);
      }
      if (filters.status) {
        params.append('status', filters.status);
      }

      const response = await HttpInterceptor.get(`/api/appointments?${params.toString()}`);
      if (response.status === 200) {
        setAppointments(response.data.data || []);
      }
    } catch (err) {
      console.error('Fetch appointments error:', err);
      setAppointments([]);
    } finally {
      setIsLoading(false);
    }
  }, [activeService, filters.status]);

  // Fetch programs and students/appointments on component mount and when filters change
  useEffect(() => {
    fetchPrograms();
    getStudents();
    if (isConsultant) {
      fetchAppointments();
    }
  }, [fetchPrograms, getStudents, fetchAppointments, isConsultant]);

  // For consultants, merge unique clients from appointments into the students list
  useEffect(() => {
    if (isConsultant && appointments && appointments.length > 0) {
      // Create set of current student member IDs to avoid duplicate counting of traditional students
      // Traditional students have a planId from the Student collection
      const studentMemberIds = new Set(students.filter(s => !s.isFromAppointment).map(s => s.studentId));
      
      const appointmentStudents = [];
      const seenMemberIds = new Set();

      appointments.forEach(apt => {
        const member = apt.member;
        const memberId = member?._id || member;
        
        if (memberId && !studentMemberIds.has(memberId) && !seenMemberIds.has(memberId)) {
          seenMemberIds.add(memberId);
          
          const memberName = member?.personalDetails?.name || member?.name || 'Client';
          const memberImage = member?.personalDetails?.image || member?.image || null;

          // Map appointment to student structure for uniform display
          appointmentStudents.push({
            _id: apt._id,
            planId: `apt-${apt._id}`, // Prefix to avoid conflict and identify source
            studentId: memberId,
            name: formatName(memberName),
            email: member?.personalDetails?.email || member?.email || '',
            phone: member?.personalDetails?.phone || member?.phone || member?.mobile || '',
            status: ['confirmed', 'approved', 'completed'].includes(apt.status) ? 'active' : 'pending',
            progress: 0,
            totalSessions: 0,
            attendedSessions: 0,
            lastSession: apt.requestedDateTime,
            image: memberImage,
            programDetails: {
              name: apt.program?.name || 'Consultation',
              price: apt.feeAmount || 0,
              duration: 'N/A',
              programId: apt.program?._id || null
            },
            payment: {
              status: apt.paymentStatus || (apt.status === 'confirmed' ? 'paid' : 'pending'),
              totalAmount: apt.feeAmount || 0,
              paidAmount: (apt.paymentStatus === 'paid' || apt.status === 'confirmed') ? (apt.feeAmount || 0) : 0,
              dueAmount: (apt.paymentStatus === 'paid' || apt.status === 'confirmed') ? 0 : (apt.feeAmount || 0)
            },
            review: apt.review || null,
            joinDate: apt.createdAt || apt.requestedDateTime || new Date().toISOString(),
            isActive: true,
            isFromAppointment: true
          });
        }
      });

      if (appointmentStudents.length > 0) {
        setStudents(prev => {
          // Efficiently merge: keep only non-appointment students from previous state
          const nonAppointmentStudents = prev.filter(s => !s.isFromAppointment);
          return [...nonAppointmentStudents, ...appointmentStudents];
        });
      }
    } else if (isConsultant && appointments && appointments.length === 0) {
        // Clear appointment students if appointments are empty
        setStudents(prev => {
            const onlyRealStudents = prev.filter(s => !s.isFromAppointment);
            return onlyRealStudents.length !== prev.length ? onlyRealStudents : prev;
        });
    }
  }, [appointments, isConsultant]);

  // Debounce utility for search
  const debounce = useCallback((func, wait) => {
    let timeout;
    const debounced = (...args) => {
      clearTimeout(timeout);
      timeout = setTimeout(() => func(...args), wait);
    };
    debounced.cancel = () => {
      clearTimeout(timeout);
    };
    return debounced;
  }, []);

  // Debounced search handler
  const debouncedSearchRef = useRef(null);
  useEffect(() => {
    if (debouncedSearchRef.current?.cancel) {
      debouncedSearchRef.current.cancel();
    }
    debouncedSearchRef.current = debounce((searchValue) => {
      getStudents({ search: searchValue });
    }, DEBOUNCE_DELAY.SEARCH);

    return () => {
      if (debouncedSearchRef.current?.cancel) {
        debouncedSearchRef.current.cancel();
      }
    };
  }, [getStudents, debounce]);

  // Filter and search students
  const filteredStudents = useMemo(() => {
    if (!searchTerm && !Object.values(filters).some(v => v !== '' && v !== null)) {
      return students;
    }

    return students.filter(student => {
      // Search term filter
      if (searchTerm) {
        const searchLower = searchTerm.toLowerCase();
        const matchesSearch =
          student.name?.toLowerCase().includes(searchLower) ||
          student.email?.toLowerCase().includes(searchLower) ||
          student.phone?.includes(searchTerm) ||
          student.studentId?.toLowerCase().includes(searchLower);
        if (!matchesSearch) return false;
      }

      // Program filter
      if (filters.program && student.programDetails?.name !== filters.program) {
        const program = programs.find(p => p._id === filters.program || p.name === filters.program);
        if (!program || student.programDetails?.name !== program.name) {
          return false;
        }
      }

      // Status filter
      if (filters.status && student.status !== filters.status) {
        return false;
      }

      // Progress filters
      if (filters.minProgress && (student.progress || 0) < Number(filters.minProgress)) {
        return false;
      }
      if (filters.maxProgress && (student.progress || 0) > Number(filters.maxProgress)) {
        return false;
      }

      // Review filter
      if (filters.hasReview === 'yes' && !student.review?.rating) {
        return false;
      }
      if (filters.hasReview === 'no' && student.review?.rating) {
        return false;
      }

      return true;
    });
  }, [students, searchTerm, filters, programs]);

  // Sort students
  const sortedStudents = useMemo(() => {
    const filtered = filteredStudents || [];

    // Sort pending students first
    const pendingFirst = [...filtered].sort((a, b) => {
      if (a.status === 'pending' && b.status !== 'pending') return -1;
      if (a.status !== 'pending' && b.status === 'pending') return 1;
      return 0;
    });

    // Apply additional sorting
    return pendingFirst.sort((a, b) => {
      if (sortConfig.key === 'name') {
        const nameA = (a.name || '').toLowerCase();
        const nameB = (b.name || '').toLowerCase();
        return sortConfig.direction === 'ascending'
          ? nameA.localeCompare(nameB)
          : nameB.localeCompare(nameA);
      } else if (sortConfig.key === 'joinDate') {
        const dateA = new Date(a.joinDate || 0).getTime();
        const dateB = new Date(b.joinDate || 0).getTime();
        return sortConfig.direction === 'ascending'
          ? dateA - dateB
          : dateB - dateA;
      }
      return 0;
    });
  }, [filteredStudents, sortConfig]);

  // Calculate pagination
  const totalPages = useMemo(() => {
    if (itemsPerPage === 'all') return 1;
    return Math.ceil(sortedStudents.length / Number(itemsPerPage));
  }, [sortedStudents.length, itemsPerPage]);

  const paginatedStudents = useMemo(() => {
    if (itemsPerPage === 'all') return sortedStudents;
    const startIndex = (currentPage - 1) * Number(itemsPerPage);
    const endIndex = startIndex + Number(itemsPerPage);
    return sortedStudents.slice(startIndex, endIndex);
  }, [sortedStudents, currentPage, itemsPerPage]);

  // Helper function to get active filters as array of objects
  const getActiveFilters = useCallback(() => {
    const activeFilters = [];

    if (searchTerm.trim()) {
      activeFilters.push({
        key: 'search',
        label: 'Search',
        value: searchTerm,
        displayValue: `"${searchTerm}"`
      });
    }

    if (filters.program) {
      const program = programs.find(p => p._id === filters.program || p.name === filters.program);
      activeFilters.push({
        key: 'program',
        label: 'Program',
        value: filters.program,
        displayValue: program?.name || filters.program
      });
    }

    if (filters.status) {
      activeFilters.push({
        key: 'status',
        label: 'Status',
        value: filters.status,
        displayValue: filters.status.charAt(0).toUpperCase() + filters.status.slice(1)
      });
    }

    if (filters.minProgress) {
      activeFilters.push({
        key: 'minProgress',
        label: 'Min Progress',
        value: filters.minProgress,
        displayValue: `${filters.minProgress}%`
      });
    }

    if (filters.maxProgress) {
      activeFilters.push({
        key: 'maxProgress',
        label: 'Max Progress',
        value: filters.maxProgress,
        displayValue: `${filters.maxProgress}%`
      });
    }

    if (filters.hasReview === 'true') {
      activeFilters.push({
        key: 'hasReview',
        label: 'Has Review',
        value: 'true',
        displayValue: 'With Reviews'
      });
    } else if (filters.hasReview === 'false') {
      activeFilters.push({
        key: 'hasReview',
        label: 'Has Review',
        value: 'false',
        displayValue: 'Without Reviews'
      });
    }

    return activeFilters;
  }, [searchTerm, filters, programs]);

  // Render empty state based on scenario
  const renderEmptyState = useCallback(() => {
    const hasActiveFilters = getActiveFilters().length > 0;
    const hasSearchTerm = searchTerm.trim().length > 0;
    const totalStudents = students.length; // Original unfiltered count
    const activeFilters = getActiveFilters();

    // Scenario 1: No students at all (initial state)
    if (totalStudents === 0 && !hasActiveFilters && !hasSearchTerm) {
      return (
        <div className={`${isPWA ? 'bg-gradient-to-br from-white to-orange-50/30 border-2 border-orange-200/50 shadow-xl' : 'bg-white rounded-xl shadow-sm'} p-12 text-center ${isPWA ? 'rounded-2xl' : ''}`}>
          <FaUserFriends className={`mx-auto h-16 w-16 ${isPWA ? 'text-orange-300' : 'text-gray-400'} mb-4 ${isPWA ? 'opacity-60' : ''}`} />
          <h3 className={`text-xl font-semibold ${isPWA ? 'text-orange-800' : 'text-gray-900'} mb-2`}>{isConsultant ? 'No Clients Yet' : 'No Students Yet'}</h3>
          <p className={`${isPWA ? 'text-orange-700' : 'text-gray-600'} mb-6 max-w-md mx-auto`}>
            {isConsultant ? 'Start building your client base by adding your first client' : 'Start building your student base by adding your first student'}
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <button
              onClick={() => setShowAddModal(true)}
              className={`px-6 py-3 ${isPWA ? 'bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 text-white shadow-lg hover:shadow-xl' : 'bg-orange-600 hover:bg-orange-700 text-white'} rounded-lg transition-all duration-200 ${isPWA ? 'font-medium' : 'shadow-sm'} flex items-center justify-center gap-2`}
            >
              <FaUserPlus />
              {isConsultant ? 'Add Client' : 'Add Student'}
            </button>
            <button
              onClick={() => setShowBulkImportModal(true)}
              className={`px-6 py-3 ${isPWA ? 'bg-white text-orange-700 border-2 border-orange-200 hover:bg-orange-50' : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'} rounded-lg transition-colors font-medium flex items-center justify-center gap-2 ${isPWA ? 'shadow-md' : ''}`}
            >
              <FaFileImport />
              Import from CSV
            </button>
          </div>
        </div>
      );
    }

    // Scenario 2: All students filtered out
    if (totalStudents > 0 && sortedStudents.length === 0 && hasActiveFilters) {
      return (
        <div className={`${isPWA ? 'bg-gradient-to-br from-white to-orange-50/30 border-2 border-orange-200/50 shadow-xl' : 'bg-white rounded-xl shadow-sm'} p-12 text-center ${isPWA ? 'rounded-2xl' : ''}`}>
          <FaFilter className={`mx-auto h-16 w-16 ${isPWA ? 'text-orange-300' : 'text-gray-400'} mb-4 ${isPWA ? 'opacity-60' : ''}`} />
          <h3 className={`text-xl font-semibold ${isPWA ? 'text-orange-800' : 'text-gray-900'} mb-2`}>{isConsultant ? 'No Clients Match Your Filters' : 'No Students Match Your Filters'}</h3>
          <p className={`${isPWA ? 'text-orange-700' : 'text-gray-600'} mb-4 max-w-md mx-auto`}>
            Try adjusting your search or filter criteria to see more results. You can clear individual filters by clicking the × button on each filter chip, or reset all filters using the button below.
          </p>

          {/* Active Filter Chips */}
          {activeFilters.length > 0 && (
            <div className="mb-6 flex flex-wrap gap-2 justify-center">
              {activeFilters.map((filter) => (
                <span
                  key={filter.key}
                  className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${isPWA ? 'bg-gradient-to-r from-orange-100 to-amber-100 text-orange-800 shadow-sm' : 'bg-blue-100 text-blue-800'}`}
                >
                  {filter.label}: {filter.displayValue}
                  <button
                    onClick={() => {
                      if (filter.key === 'search') {
                        setSearchTerm('');
                      } else {
                        setFilters(prev => ({ ...prev, [filter.key]: '' }));
                      }
                    }}
                    className={`ml-2 ${isPWA ? 'text-orange-600 hover:text-orange-800' : 'text-orange-600 hover:text-orange-800'}`}
                    aria-label={`Remove ${filter.label} filter`}
                  >
                    <FaTimes className="h-3 w-3" />
                  </button>
                </span>
              ))}
            </div>
          )}

          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <button
              onClick={() => {
                setSearchTerm('');
                setFilters({
                  program: '',
                  status: '',
                  minProgress: '',
                  maxProgress: '',
                  hasReview: '',
                });
              }}
              className={`px-6 py-3 ${isPWA ? 'bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 text-white shadow-lg hover:shadow-xl' : 'bg-blue-600 hover:bg-blue-700 text-white'} rounded-lg transition-all duration-200 ${isPWA ? 'font-medium' : ''}`}
            >
              Clear All Filters
            </button>
            <button
              onClick={() => setShowAddModal(true)}
              className={`px-6 py-3 ${isPWA ? 'bg-white text-orange-700 border-2 border-orange-200 hover:bg-orange-50' : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'} rounded-lg transition-colors font-medium flex items-center justify-center gap-2 ${isPWA ? 'shadow-md' : ''}`}
            >
              <FaUserPlus />
              Add New Student
            </button>
          </div>
        </div>
      );
    }

    // Scenario 3: Search returns no results
    if (hasSearchTerm && sortedStudents.length === 0) {
      return (
        <div className={`${isPWA ? 'bg-gradient-to-br from-white to-orange-50/30 border-2 border-orange-200/50 shadow-xl' : 'bg-white rounded-xl shadow-sm'} p-12 text-center ${isPWA ? 'rounded-2xl' : ''}`}>
          <FaSearch className={`mx-auto h-16 w-16 ${isPWA ? 'text-orange-300' : 'text-gray-400'} mb-4 ${isPWA ? 'opacity-60' : ''}`} />
          <h3 className={`text-xl font-semibold ${isPWA ? 'text-orange-800' : 'text-gray-900'} mb-2`}>No Results Found</h3>
          <p className={`${isPWA ? 'text-orange-700' : 'text-gray-600'} mb-6 max-w-md mx-auto`}>
            {isConsultant ? 'No clients found matching' : 'No students found matching'} <span className="font-medium">"{searchTerm}"</span>. {isConsultant ? 'Try different search terms or clear the search to see all clients.' : 'Try different search terms or clear the search to see all students.'}
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <button
              onClick={() => setSearchTerm('')}
              className={`px-6 py-3 ${isPWA ? 'bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 text-white shadow-lg hover:shadow-xl' : 'bg-blue-600 hover:bg-blue-700 text-white'} rounded-lg transition-all duration-200 ${isPWA ? 'font-medium' : ''}`}
            >
              Clear Search
            </button>
            <button
              onClick={() => setShowAddModal(true)}
              className={`px-6 py-3 ${isPWA ? 'bg-white text-orange-700 border-2 border-orange-200 hover:bg-orange-50' : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'} rounded-lg transition-colors font-medium flex items-center justify-center gap-2 ${isPWA ? 'shadow-md' : ''}`}
            >
              <FaUserPlus />
              Add New Student
            </button>
          </div>
        </div>
      );
    }

    // Fallback empty state
    return (
      <div className={`${isPWA ? 'bg-gradient-to-br from-white to-orange-50/30 border-2 border-orange-200/50 shadow-xl' : 'bg-white rounded-xl shadow-sm'} p-12 text-center ${isPWA ? 'rounded-2xl' : ''}`}>
        <FaUserFriends className={`mx-auto h-12 w-12 ${isPWA ? 'text-orange-300' : 'text-gray-400'} mb-4 ${isPWA ? 'opacity-60' : ''}`} />
        <p className={`${isPWA ? 'text-orange-700' : 'text-gray-500'} text-lg`}>{isConsultant ? 'No clients found matching your criteria' : 'No students found matching your criteria'}</p>
      </div>
    );
  }, [students.length, sortedStudents.length, searchTerm, filters, programs, getActiveFilters, isPWA]);

  const requestSort = useCallback((key) => {
    setSortConfig(prev => {
      let direction = 'ascending';
      if (prev.key === key && prev.direction === 'ascending') {
        direction = 'descending';
      }
      return { key, direction };
    });
  }, []);

  const handleApprove = async (planId) => {
    await updateStudentStatus(planId, {
      status: 'active',
      joinDate: new Date().toISOString().split('T')[0],
    });
  };

  const handleReject = async (planId) => {
    const student = students.find(s => s.planId === planId);
    setDeleteItem({ type: 'reject', id: planId, name: student?.name || 'this student' });
    setShowDeleteModal(true);
  };

  const handleAddStudent = async (e) => {
    e.preventDefault();
    setIsAddingStudent(true);
    try {
      const selectedProgram = programs.find((p) => p._id === newStudent.program);
      if (!selectedProgram) {
        setError('No valid program selected');
        return;
      }
      await addStudent({
        name: newStudent.name,
        email: newStudent.email,
        phone: newStudent.phone,
        gender: newStudent.gender,
        programId: selectedProgram._id,
        programDetails: {
          name: selectedProgram.name,
          price: selectedProgram.price || 0,
          duration: selectedProgram.duration || 'N/A',
        },
        status: 'active',
      });
      setNewStudent({ name: '', email: '', phone: '', gender: 'other', program: '', programDetails: { name: '', price: 0, duration: '' } });
      setShowAddModal(false);
    } catch (err) {
      console.error('Handle add student error:', err);
    } finally {
      setIsAddingStudent(false);
    }
  };

  const handleBulkImport = async (e) => {
    e.preventDefault();
    try {
      // Parse CSV data
      const lines = csvData.split('\n');
      const headers = lines[0].split(',').map(h => h.trim());
      const importedStudents = [];

      for (let i = 1; i < lines.length; i++) {
        if (!lines[i].trim()) continue;

        const values = lines[i].split(',');
        const studentData = {};

        headers.forEach((header, index) => {
          studentData[header] = values[index] ? values[index].trim() : '';
        });

        // Find the program by name
        const program = programs.find(p => p.name === studentData.program);
        if (!program) {
          throw new Error(`Program not found: ${studentData.program}`);
        }

        importedStudents.push({
          name: studentData.name,
          email: studentData.email,
          phone: studentData.phone,
          gender: studentData.gender || 'other',
          programId: program._id,
          programDetails: {
            name: program.name,
            price: program.price || 0,
            duration: program.duration || 'N/A',
          },
          status: 'active',
        });
      }

      await bulkImportStudents(importedStudents);
      setCsvData('');
      setShowBulkImportModal(false);
    } catch (err) {
      setError(err.message);
      console.error('Bulk import error:', err);
    }
  };

  const toggleReviewVisibility = async (planId) => {
    // Free trainers cannot use advance features - must have advance subscription plan first
    if (trainerSubscriptionPlan !== 'advance') {
      toast.error('This feature is only available in the advance plan. Please upgrade to hide/show testimonials.');
      return;
    }

    // Check if the specific feature is enabled by admin (only for advance plan trainers)
    if (!advanceFeatures?.showHideTestimonials) {
      toast.error('This feature needs to be enabled by admin. Please contact admin to enable hide/show testimonials feature.');
      return;
    }

    const student = students.find((s) => s.planId === planId);

    // Validate that student has a review with rating and is active
    if (!student.review || !student.review.rating) {
      toast.error('Cannot toggle visibility: Student has not provided a review yet.');
      return;
    }

    if (student.status !== 'active') {
      toast.error('Cannot toggle visibility: Student must be approved (active) before testimonials can be shown/hidden.');
      return;
    }

    try {
      // Only update visibility - trainers cannot edit review content
      await updateStudentReview(planId, {
        visible: !student.review.visible,
      });
    } catch (err) {
      console.error('Toggle visibility error:', err);
      const errorMsg = err.response?.data?.message || err.message || 'Failed to update review visibility';
      toast.error(errorMsg);
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
  };


  // Fetch class attendance for a student
  const fetchClassAttendance = async (studentId, classId) => {
    try {
      const response = await HttpInterceptor.get(`/api/classes/${classId}/attendance`, {
        params: { studentId }
      });
      if (response.status === 200) {
        setClassAttendance(response.data?.attendance || []);
        setShowAttendanceModal({ studentId, classId });
      }
    } catch (error) {
      console.error('Error fetching class attendance:', error);
      toast.error('Failed to load attendance');
    }
  };

  // Handle invite student
  const handleInviteStudent = async (e) => {
    e.preventDefault();
    setIsInviting(true);
    try {
      if (!inviteData.contact || !inviteData.programId) {
        toast.error('Please provide contact information and select a program');
        return;
      }

      // Validate email format if email type
      if (inviteData.contactType === 'email') {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(inviteData.contact)) {
          toast.error('Please enter a valid email address');
          return;
        }
      }

      // Validate WhatsApp format (should be phone number)
      if (inviteData.contactType === 'whatsapp') {
        const phoneRegex = /^[+]?[(]?[0-9]{1,4}[)]?[-\s.]?[(]?[0-9]{1,4}[)]?[-\s.]?[0-9]{1,9}$/;
        if (!phoneRegex.test(inviteData.contact.replace(/\s/g, ''))) {
          toast.error('Please enter a valid phone number for WhatsApp');
          return;
        }
      }

      const response = await HttpInterceptor.post('/api/students/invite', {
        contact: inviteData.contact,
        contactType: inviteData.contactType,
        programId: inviteData.programId,
        message: inviteData.message || `You've been invited to join a program!`
      });

      if (response.status === 201) {
        const responseData = response.data?.data || response.data;

        if (inviteData.contactType === 'email') {
          toast.success(`Invitation sent to ${inviteData.contact} via email`);
        } else {
          // For WhatsApp, show the link and allow opening
          const whatsappLink = responseData?.whatsappLink;
          if (whatsappLink) {
            // Open WhatsApp link in new tab
            window.open(whatsappLink, '_blank');
            toast.success(`WhatsApp link opened! If it didn't open, click here to send invitation to ${inviteData.contact}`, {
              autoClose: 8000
            });
          } else {
            toast.success(`WhatsApp invitation link generated for ${inviteData.contact}`);
          }
        }

        setInviteData({
          contact: '',
          contactType: 'email',
          programId: '',
          message: ''
        });
        setShowInviteModal(false);
        // Refresh students list
        getStudents();
      }
    } catch (error) {
      console.error('Error inviting student:', error);
      const friendlyError = getContextualError('invite student', error);
      toast.error(friendlyError);
    } finally {
      setIsInviting(false);
    }
  };

  const resetFilters = () => {
    setFilters({
      program: '',
      status: '',
      minProgress: '',
      maxProgress: '',
      hasReview: '',
    });
    setSearchTerm('');
    setActiveTab('all');
  };


  // Render star rating
  const renderStars = (rating) => {
    return <StarRating rating={rating || 0} size="sm" />;
  };

  // Progress bar component
  const ProgressBar = ({ progress, isPWA: isPWAProp }) => {
    const progressValue = Math.min(Math.max(progress || 0, 0), 100);
    return (
      <div className={`w-full ${isPWAProp ? 'bg-blue-100/50 border border-blue-200/50' : 'bg-gray-200'} rounded-full ${isPWAProp ? 'h-3 shadow-inner' : 'h-2.5'} overflow-hidden`}>
        <div
          className={`${isPWAProp ? 'bg-gradient-to-r from-blue-500 via-indigo-500 to-blue-600' : 'bg-orange-600'} ${isPWAProp ? 'h-3 shadow-md' : 'h-2.5'} rounded-full transition-all duration-500 relative overflow-hidden`}
          style={{ width: `${progressValue}%` }}
        >
          {isPWAProp && progressValue > 0 && (
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent animate-pulse"></div>
          )}
        </div>
      </div>
    );
  };

  // Handle deletion with confirmation
  const handleSendReminder = async (planId) => {
    try {
      const student = students.find(s => s.planId === planId);
      if (!student?._id) {
        toast.error(isConsultant ? 'Client ID not found' : 'Student ID not found');
        return;
      }

      setSendingReminder(planId);
      const response = await HttpInterceptor.post('/api/fee-plan/reminders/send', {
        studentId: student._id // Use Student document _id, not User _id
      });
      const studentName = student?.name || 'Student';

      // Check notification status
      const data = response.data?.data || response.data;
      const sentMethods = [];
      if (data?.emailSent) sentMethods.push('Email');
      if (data?.whatsappSent) sentMethods.push('WhatsApp');

      if (sentMethods.length > 0) {
        toast.success(`✓ Payment reminder sent to ${studentName} via ${sentMethods.join(' and ')}`);
      } else {
        toast.success(`✓ Payment reminder sent to ${studentName}`);
      }

      // Show warning if WhatsApp failed (only for advance plan users)
      if (data?.whatsappSent === false && data?.whatsappError) {
        toast.warning(
          <div>
            <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>
              ⚠️ WhatsApp Reminder Failed
            </div>
            <div style={{ fontSize: '14px' }}>
              {data.whatsappError}
            </div>
          </div>,
          {
            autoClose: 8000,
            style: { maxWidth: '400px' }
          }
        );
      }
    } catch (error) {
      console.error('Error sending reminder:', error);
      const student = students.find(s => s.planId === planId);
      const studentName = student?.name || 'Student';

      // Handle "no pending transaction" case gracefully
      if (error.response?.status === 404 && error.response?.data?.message?.includes('No pending transaction')) {
        toast.info(`${studentName} has no pending payments to remind about.`);
      } else {
        const friendlyError = getContextualError('send reminder', error);
        toast.error(friendlyError);
      }
    } finally {
      setSendingReminder(null);
    }
  };

  const handleRemoveStudent = async (planId, studentName) => {
    setDeleteItem({ type: 'student', id: planId, name: studentName });
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    setIsLoading(true);
    try {
      const itemName = deleteItem.name;
      const itemType = deleteItem.type;

      if (itemType === 'reject') {
        // For rejection, update status to rejected
        await updateStudentStatus(deleteItem.id, { status: 'rejected' });
        toast.success(`✓ ${itemName} has been rejected`);
        announceToScreenReader(`${itemName} has been rejected`);
      } else {
        // For removal, delete the student
        await removeStudent(deleteItem.id);
        toast.success(`✓ ${itemName} has been removed`);
        announceToScreenReader(`${itemName} has been removed`);
      }

      setShowDeleteModal(false);
      setDeleteItem(null);
    } catch (err) {
      const errorMsg = getContextualError(itemType === 'reject' ? 'update status' : 'remove student', err);
      setError(errorMsg);
      console.error('Delete/reject student error:', err);
      toast.error(errorMsg);
    } finally {
      setIsLoading(false);
    }
  };

  const cancelDelete = () => {
    setShowDeleteModal(false);
    setDeleteItem(null);
  };

  // Helper function to safely format dates using user's date format settings
  const formatDate = (dateValue) => {
    if (!dateValue) return 'N/A';
    try {
      return formatDateUtil(dateValue, dateFormat);
    } catch (error) {
      console.error('Date formatting error:', error);
      return 'N/A';
    }
  };

  // Helper function to check if image is a default/placeholder image
  // If so, return null to show initials instead
  const getAvatarImage = (imageSrc) => {
    // Return null for empty, null, or undefined
    if (!imageSrc || typeof imageSrc !== 'string' || !imageSrc.trim()) {
      return null;
    }

    const normalizedSrc = imageSrc.trim();

    // List of default/placeholder image paths to filter out
    const defaultImagePaths = [
      '/src/images/avatar.webp',
      '/src/images/avatar-fem.jpg',
      '/images/default-profile.jpg',
      '/images/avatar.webp',
      '/images/avatar-fem.jpg',
      'avatar.webp',
      'avatar-fem.jpg',
      'default-profile.jpg',
      'src/images/avatar',
      'images/avatar',
      'images/default'
    ];

    // Check if the image source contains any default path
    const isDefaultImage = defaultImagePaths.some(defaultPath =>
      normalizedSrc.includes(defaultPath) || normalizedSrc.endsWith(defaultPath)
    );

    // Also check if it's a path in the images folder that looks like a default avatar
    if (normalizedSrc.includes('/images/') || normalizedSrc.includes('images/')) {
      const filename = normalizedSrc.split('/').pop() || normalizedSrc.split('\\').pop() || '';
      const filenameLower = filename.toLowerCase();
      if (filenameLower.includes('avatar') || filenameLower.includes('default')) {
        return null;
      }
    }

    // Return null for default images, otherwise return the original source
    return isDefaultImage ? null : normalizedSrc;
  };

  // Close action menu when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (openActionMenu) {
        const menuContainer = event.target.closest('[data-action-menu]');
        const menuDropdown = event.target.closest('[data-action-dropdown]');
        if (!menuContainer && !menuDropdown) {
          setOpenActionMenu(null);
        }
      }
    }
    if (openActionMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [openActionMenu]);

  return (
    <>
      {/* Skip to main content link for accessibility */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-50 focus:px-4 focus:py-2 focus:bg-orange-600 focus:text-white focus:rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
        aria-label="Skip to main content"
      >
        Skip to main content
      </a>

      {/* Screen reader announcement region */}
      <div
        ref={screenReaderAnnouncement}
        className="sr-only"
        role="status"
        aria-live="polite"
        aria-atomic="true"
      />

      <div className={`min-h-screen ${isPWA ? 'bg-gradient-to-b from-orange-50/40 via-amber-50/30 to-white' : 'bg-gray-50'} p-4 md:p-6 lg:p-8`} id="main-content" role="main">
        {/* Error Display */}
        {error && (
          <div className="mb-4 p-4 bg-red-100 text-red-700 rounded-lg">
            {error}
          </div>
        )}

        {/* Header */}
        <div className={`relative ${isPWA ? 'bg-gradient-to-br from-orange-50 via-amber-50 to-yellow-50 border-2 border-orange-200/50 shadow-xl' : 'bg-gradient-to-r from-orange-600 to-amber-700'} ${isPWA ? 'text-gray-900' : 'text-white'} ${isPWA ? 'rounded-2xl mb-6 sm:mb-8 overflow-hidden' : 'rounded-xl mb-8 overflow-hidden'}`}>
          {isPWA && (
            <>
              <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-orange-400 via-amber-400 to-yellow-400"></div>
              <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-orange-200/30 to-amber-200/20 rounded-full blur-2xl"></div>
            </>
          )}
          {!isPWA && (
            <div className="absolute inset-0 bg-black opacity-20"></div>
          )}
          <div className={`${isPWA ? 'relative px-4 sm:px-6 lg:px-8 py-8 sm:py-10 pb-12 sm:pb-14' : 'p-6'} flex flex-col md:flex-row justify-between items-start md:items-center ${isPWA ? 'z-10' : ''}`}>
            <div className={isPWA ? 'relative z-10' : ''}>
              <h1 className={`${isPWA ? 'text-xl sm:text-2xl' : 'text-2xl sm:text-3xl'} font-bold ${isPWA ? 'text-orange-800' : ''} ${isPWA ? 'mb-3' : 'mb-2'} flex items-center gap-2`}>
                <div className={isPWA ? 'bg-gradient-to-br from-orange-500 to-amber-600 p-2 rounded-lg shadow-md' : ''}>
                  <FaUserFriends className={`${isPWA ? 'text-white text-base' : 'text-white'}`} />
                </div>
                <span>{isConsultant ? 'My Clients' : 'My Members'}</span>
              </h1>
              <p className={`${isPWA ? 'text-sm sm:text-base text-orange-700' : 'text-white/90'} ${isPWA ? 'mt-1' : 'mt-2'}`}>
                {isConsultant ? 'Manage your clients and their details' : 'Manage your students and their testimonials'}
              </p>
            </div>
            <div className={`flex flex-wrap gap-2 sm:gap-3 mt-4 md:mt-0 ${isPWA ? 'relative z-10' : ''}`}>
              {!isConsultant && (
                <>
                  <button
                    onClick={() => setShowBulkImportModal(true)}
                    className={`flex items-center ${isPWA ? 'bg-gradient-to-r from-emerald-500 to-green-500 hover:from-emerald-600 hover:to-green-600 text-white shadow-md hover:shadow-lg border border-emerald-400/50' : 'bg-green-600 hover:bg-green-700 text-white'} px-4 py-2 rounded-lg transition-all duration-200 ${isPWA ? 'font-medium' : ''} focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2`}
                    title="Import multiple students from a CSV file"
                    aria-label={isConsultant ? "Bulk import clients from CSV file" : "Bulk import students from CSV file"}
                  >
                    <FaFileImport className="mr-2" aria-hidden="true" />
                    Bulk Import
                  </button>
                  <button
                    onClick={() => setShowInviteModal(true)}
                    className={`flex items-center ${isPWA ? 'bg-gradient-to-r from-emerald-500 to-green-500 hover:from-emerald-600 hover:to-green-600 text-white shadow-md hover:shadow-lg border border-emerald-400/50' : 'bg-green-600 hover:bg-green-700 text-white'} px-4 py-2 rounded-lg transition-all duration-200 ${isPWA ? 'font-medium' : ''} focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2`}
                    title="Send invitation to student via email or WhatsApp"
                    aria-label={isConsultant ? "Invite client via email or WhatsApp" : "Invite student via email or WhatsApp"}
                  >
                    <FaEnvelope className="mr-2" aria-hidden="true" />
                    Invite Student
                  </button>
                  <button
                    onClick={() => setShowAddModal(true)}
                    className={`flex items-center ${isPWA ? 'bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 text-white shadow-md hover:shadow-lg border border-orange-400/50' : 'bg-orange-600 hover:bg-orange-700 text-white'} px-4 py-2 rounded-lg transition-all duration-200 ${isPWA ? 'font-medium' : 'shadow-sm'} focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2`}
                    title="Add a new student manually"
                    aria-label={isConsultant ? "Add new client" : "Add new student"}
                  >
                    <FaUserPlus className="mr-2" aria-hidden="true" />
                    {isConsultant ? 'Add Client' : 'Add Student'}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        <div className={`grid ${isPWA ? 'grid-cols-2' : 'grid-cols-1 md:grid-cols-4'} ${isPWA ? 'gap-3 sm:gap-4' : 'gap-4 sm:gap-6'} mb-6`}>
          {/* Total Students Card */}
          <div className={`${isPWA ? 'bg-gradient-to-br from-white via-orange-50/40 to-orange-100/30 border-2 border-orange-200/60 shadow-xl hover:shadow-2xl hover:border-orange-300/80' : 'bg-white rounded-xl shadow-sm hover:shadow-md'} ${isPWA ? 'p-4 sm:p-5 rounded-2xl relative overflow-hidden group' : 'p-6'} transition-all duration-300`}>
            {isPWA && (
              <>
                <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-orange-400 via-amber-400 to-yellow-400"></div>
                <div className="absolute top-0 right-0 w-24 h-24 bg-gradient-to-br from-orange-200/40 to-amber-200/30 rounded-full blur-2xl opacity-60 group-hover:opacity-100 transition-opacity duration-300"></div>
                <div className="absolute bottom-0 left-0 w-16 h-16 bg-gradient-to-tr from-orange-100/30 to-transparent rounded-full blur-xl"></div>
              </>
            )}
            <div className={isPWA ? 'relative z-10' : ''}>
              <div className={`flex items-center justify-between ${isPWA ? 'mb-3' : 'mb-2'}`}>
                <h3 className={`${isPWA ? 'text-orange-700 text-[10px] sm:text-xs uppercase tracking-wider font-bold' : 'text-gray-500 text-sm font-medium'}`}>{isConsultant ? 'Total Clients' : 'Total Students'}</h3>
                {isPWA && (
                  <div className="bg-gradient-to-br from-orange-500 to-amber-600 p-1.5 sm:p-2 rounded-lg shadow-md">
                    <FaUserFriends className="text-white text-xs sm:text-sm" />
                  </div>
                )}
              </div>
              <p className={`${isPWA ? 'text-2xl sm:text-3xl' : 'text-3xl'} font-bold ${isPWA ? 'text-orange-600' : 'text-purple-600'} ${isPWA ? 'mt-1' : 'mt-1'}`}>
                {isConsultant 
                  ? new Set(appointments.filter(a => ['confirmed', 'approved', 'completed'].includes(a.status)).map(a => a.member?._id || a.member)).size
                  : students.filter((s) => s.status === 'active').length}
              </p>
              {isPWA && (
                <p className="text-[9px] sm:text-xs text-orange-600/70 mt-1 font-medium">
                  {isConsultant ? 'Confirmed appointments' : 'Active members'}
                </p>
              )}
            </div>
          </div>

          {/* Pending Requests Card */}
          <div className={`${isPWA ? 'bg-gradient-to-br from-white via-amber-50/40 to-yellow-100/30 border-2 border-amber-200/60 shadow-xl hover:shadow-2xl hover:border-amber-300/80' : 'bg-white rounded-xl shadow-sm hover:shadow-md'} ${isPWA ? 'p-4 sm:p-5 rounded-2xl relative overflow-hidden group' : 'p-6'} transition-all duration-300`}>
            {isPWA && (
              <>
                <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-amber-400 via-yellow-400 to-orange-400"></div>
                <div className="absolute top-0 right-0 w-24 h-24 bg-gradient-to-br from-amber-200/40 to-yellow-200/30 rounded-full blur-2xl opacity-60 group-hover:opacity-100 transition-opacity duration-300"></div>
                <div className="absolute bottom-0 left-0 w-16 h-16 bg-gradient-to-tr from-amber-100/30 to-transparent rounded-full blur-xl"></div>
              </>
            )}
            <div className={isPWA ? 'relative z-10' : ''}>
              <div className={`flex items-center justify-between ${isPWA ? 'mb-3' : 'mb-2'}`}>
                <h3 className={`${isPWA ? 'text-amber-700 text-[10px] sm:text-xs uppercase tracking-wider font-bold' : 'text-gray-500 text-sm font-medium'}`}>
                  {isConsultant ? 'Pending Appointments' : 'Pending Requests'}
                </h3>
                {isPWA && (
                  <div className="bg-gradient-to-br from-amber-500 to-yellow-600 p-1.5 sm:p-2 rounded-lg shadow-md">
                    <FaClock className="text-white text-xs sm:text-sm" />
                  </div>
                )}
              </div>
              <p className={`${isPWA ? 'text-2xl sm:text-3xl' : 'text-3xl'} font-bold ${isPWA ? 'text-amber-600' : 'text-yellow-600'} ${isPWA ? 'mt-1' : 'mt-1'}`}>
                {isConsultant
                  ? appointments.filter(a => a.status === 'pending').length
                  : students.filter((s) => s.status === 'pending').length}
              </p>
              {isPWA && (
                <p className="text-[9px] sm:text-xs text-amber-600/70 mt-1 font-medium">
                  {isConsultant ? 'Awaiting your approval' : 'Awaiting approval'}
                </p>
              )}
            </div>
          </div>

          {/* With Reviews Card */}
          <div className={`${isPWA ? 'bg-gradient-to-br from-white via-emerald-50/40 to-green-100/30 border-2 border-emerald-200/60 shadow-xl hover:shadow-2xl hover:border-emerald-300/80' : 'bg-white rounded-xl shadow-sm hover:shadow-md'} ${isPWA ? 'p-4 sm:p-5 rounded-2xl relative overflow-hidden group' : 'p-6'} transition-all duration-300`}>
            {isPWA && (
              <>
                <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-emerald-400 via-green-400 to-teal-400"></div>
                <div className="absolute top-0 right-0 w-24 h-24 bg-gradient-to-br from-emerald-200/40 to-green-200/30 rounded-full blur-2xl opacity-60 group-hover:opacity-100 transition-opacity duration-300"></div>
                <div className="absolute bottom-0 left-0 w-16 h-16 bg-gradient-to-tr from-emerald-100/30 to-transparent rounded-full blur-xl"></div>
              </>
            )}
            <div className={isPWA ? 'relative z-10' : ''}>
              <div className={`flex items-center justify-between ${isPWA ? 'mb-3' : 'mb-2'}`}>
                <h3 className={`${isPWA ? 'text-emerald-700 text-[10px] sm:text-xs uppercase tracking-wider font-bold' : 'text-gray-500 text-sm font-medium'}`}>With Reviews</h3>
                {isPWA && (
                  <div className="bg-gradient-to-br from-emerald-500 to-green-600 p-1.5 sm:p-2 rounded-lg shadow-md">
                    <FaStar className="text-white text-xs sm:text-sm" />
                  </div>
                )}
              </div>
              <p className={`${isPWA ? 'text-2xl sm:text-3xl' : 'text-3xl'} font-bold ${isPWA ? 'text-emerald-600' : 'text-green-600'} ${isPWA ? 'mt-1' : 'mt-1'}`}>
                {/* Only count students who have actually submitted reviews (with rating) */}
                {/* Default rating is not a student review, so exclude it from count */}
                {students.filter((s) => s.review && s.review.rating).length}
              </p>
              {isPWA && (
                <p className="text-[9px] sm:text-xs text-emerald-600/70 mt-1 font-medium">Student feedback</p>
              )}
            </div>
          </div>

          {/* Visible Testimonials Card */}
          <div className={`${isPWA ? 'bg-gradient-to-br from-white via-blue-50/40 to-indigo-100/30 border-2 border-blue-200/60 shadow-xl hover:shadow-2xl hover:border-blue-300/80' : 'bg-white rounded-xl shadow-sm hover:shadow-md'} ${isPWA ? 'p-4 sm:p-5 rounded-2xl relative overflow-hidden group' : 'p-6'} transition-all duration-300`}>
            {isPWA && (
              <>
                <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-blue-400 via-indigo-400 to-purple-400"></div>
                <div className="absolute top-0 right-0 w-24 h-24 bg-gradient-to-br from-blue-200/40 to-indigo-200/30 rounded-full blur-2xl opacity-60 group-hover:opacity-100 transition-opacity duration-300"></div>
                <div className="absolute bottom-0 left-0 w-16 h-16 bg-gradient-to-tr from-blue-100/30 to-transparent rounded-full blur-xl"></div>
              </>
            )}
            <div className={isPWA ? 'relative z-10' : ''}>
              <div className={`flex items-center justify-between ${isPWA ? 'mb-3' : 'mb-2'}`}>
                <h3 className={`${isPWA ? 'text-blue-700 text-[10px] sm:text-xs uppercase tracking-wider font-bold' : 'text-gray-500 text-sm font-medium'}`}>Visible Testimonials</h3>
                {isPWA && (
                  <div className="bg-gradient-to-br from-blue-500 to-indigo-600 p-1.5 sm:p-2 rounded-lg shadow-md">
                    <FaRegEye className="text-white text-xs sm:text-sm" />
                  </div>
                )}
              </div>
              <p className={`${isPWA ? 'text-2xl sm:text-3xl' : 'text-3xl'} font-bold ${isPWA ? 'text-blue-600' : 'text-blue-600'} ${isPWA ? 'mt-1' : 'mt-1'}`}>
                {/* Free plan trainers see all reviews as visible (except pending students) */}
                {trainerSubscriptionPlan === 'free'
                  ? students.filter((s) => s.review?.rating && s.status === 'active').length
                  : students.filter((s) => s.review?.rating && s.review?.visible).length}
              </p>
              {isPWA && (
                <p className="text-[9px] sm:text-xs text-blue-600/70 mt-1 font-medium">Public reviews</p>
              )}
            </div>
          </div>
        </div>

        {/* Tabs, Search, and Filters */}
        <div className={`${isPWA ? 'bg-gradient-to-br from-white to-orange-50/30 border-2 border-orange-200/50 shadow-xl' : 'bg-white rounded-xl shadow-sm'} ${isPWA ? 'p-3 sm:p-4' : 'p-6'} ${isPWA ? 'rounded-2xl' : ''} mb-6`}>
          <div className={`flex flex-col ${isPWA ? 'space-y-3' : 'space-y-4'}`}>
            {/* Tabs and Search Row */}
            <div className={`flex flex-col ${isPWA ? 'sm:flex-row' : 'md:flex-row'} justify-between items-start ${isPWA ? 'sm:items-center' : 'md:items-center'} ${isPWA ? 'space-y-3 sm:space-y-0 gap-3' : 'space-y-4 md:space-y-0'}`}>
              {/* Tabs - More compact in PWA */}
              <div className={`flex ${isPWA ? 'space-x-0.5' : 'space-x-1'} ${isPWA ? 'bg-orange-50/50 border border-orange-200/50' : 'bg-gray-100'} ${isPWA ? 'p-0.5' : 'p-1'} rounded-lg ${isPWA ? 'w-full sm:w-auto' : ''}`}>
                <button
                  onClick={() => {
                    setActiveTab('all');
                    getStudents({ status: '' });
                  }}
                  className={`${isPWA ? 'px-2.5 py-1.5 text-xs sm:text-sm' : 'px-4 py-2'} rounded-md transition-all duration-200 ${activeTab === 'all' ? isPWA ? 'bg-gradient-to-r from-orange-100 to-amber-100 text-orange-700 font-medium shadow-md' : 'bg-white shadow-sm' : isPWA ? 'hover:bg-orange-50 text-orange-600' : ''}`}
                >
                  {isConsultant ? 'All Clients' : 'All Students'}
                </button>
                <button
                  onClick={() => {
                    setActiveTab('active');
                    getStudents({ status: 'active' });
                  }}
                  className={`${isPWA ? 'px-2.5 py-1.5 text-xs sm:text-sm' : 'px-4 py-2'} rounded-md transition-all duration-200 ${activeTab === 'active' ? isPWA ? 'bg-gradient-to-r from-orange-100 to-amber-100 text-orange-700 font-medium shadow-md' : 'bg-white shadow-sm' : isPWA ? 'hover:bg-orange-50 text-orange-600' : ''}`}
                >
                  Active
                </button>
                <button
                  onClick={() => {
                    setActiveTab('pending');
                    getStudents({ status: 'pending' });
                  }}
                  className={`${isPWA ? 'px-2.5 py-1.5 text-xs sm:text-sm' : 'px-4 py-2'} rounded-md transition-all duration-200 ${activeTab === 'pending' ? isPWA ? 'bg-gradient-to-r from-orange-100 to-amber-100 text-orange-700 font-medium shadow-md' : 'bg-white shadow-sm' : isPWA ? 'hover:bg-orange-50 text-orange-600' : ''}`}
                >
                  Pending
                </button>
                <button
                  onClick={() => {
                    setActiveTab('withReviews');
                    getStudents({ hasReview: 'true' });
                  }}
                  className={`${isPWA ? 'px-2.5 py-1.5 text-xs sm:text-sm' : 'px-4 py-2'} rounded-md transition-all duration-200 ${activeTab === 'withReviews' ? isPWA ? 'bg-gradient-to-r from-orange-100 to-amber-100 text-orange-700 font-medium shadow-md' : 'bg-white shadow-sm' : isPWA ? 'hover:bg-orange-50 text-orange-600' : ''}`}
                >
                  With Reviews
                </button>
              </div>

              {/* Search and Filter Button Row */}
              <div className={`flex ${isPWA ? 'space-x-2' : 'space-x-3'} w-full ${isPWA ? 'sm:w-auto' : 'md:w-auto'}`}>
                <div className={`relative ${isPWA ? 'flex-1 sm:w-48' : 'w-full md:w-64'}`}>
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <FaSearch className={isPWA ? 'text-orange-400' : 'text-gray-400'} size={isPWA ? 14 : 16} />
                  </div>
                  <input
                    type="text"
                    id="student-search"
                    placeholder={isPWA ? "Search..." : (isConsultant ? "Search clients..." : "Search students...")}
                    className={`${isPWA ? 'pl-8 pr-3 py-1.5 text-xs sm:text-sm' : 'pl-10 pr-4 py-2'} w-full border ${isPWA ? 'border-2 border-orange-200 focus:border-orange-400' : 'border-gray-300'} rounded-lg focus:ring-2 ${isPWA ? 'focus:ring-orange-400' : 'focus:ring-orange-500'} ${isPWA ? 'focus:border-orange-400' : 'focus:border-orange-500'} ${isPWA ? 'bg-white/80' : 'bg-white'}`}
                    value={searchTerm}
                    onChange={(e) => {
                      const value = e.target.value;
                      setSearchTerm(value);
                      // Debounced search will be triggered automatically via useEffect
                      if (debouncedSearchRef.current) {
                        debouncedSearchRef.current(value);
                      }
                    }}
                    aria-label={isConsultant ? "Search clients by name, email, or phone" : "Search students by name, email, or phone"}
                    aria-describedby="search-help"
                  />
                  <span id="search-help" className="sr-only">{isConsultant ? "Search clients by name, email, phone number, or client ID" : "Search students by name, email, phone number, or student ID"}</span>
                  {!isPWA && (
                    <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                      <FaInfoCircle
                        className="text-gray-400 hover:text-gray-600 cursor-help"
                        title={isConsultant ? "Search works across client name, email, and phone number" : "Search works across student name, email, and phone number"}
                        aria-label={isConsultant ? "Search help: Search works across client name, email, and phone number" : "Search help: Search works across student name, email, and phone number"}
                      />
                    </div>
                  )}
                </div>
                <button
                  onClick={() => setShowFilters(!showFilters)}
                  aria-expanded={showFilters}
                  aria-controls="filters-panel"
                  aria-label={showFilters ? "Hide filters" : "Show filters"}
                  className={`flex items-center ${isPWA ? 'px-3 py-1.5 text-xs sm:text-sm' : 'px-4 py-2'} border ${isPWA ? 'border-2 border-orange-200 hover:bg-orange-50' : 'border-gray-300 hover:bg-gray-50'} rounded-lg transition-colors focus:outline-none focus:ring-2 ${isPWA ? 'focus:ring-orange-400' : 'focus:ring-blue-500'} focus:ring-offset-2 relative ${isPWA ? 'whitespace-nowrap' : ''}`}
                  title={isConsultant ? "Filter clients by program, status, progress, or review" : "Filter students by program, status, progress, or review"}
                >
                  <FaFilter className={`${isPWA ? 'mr-1.5' : 'mr-2'} ${isPWA ? 'text-orange-500' : 'text-gray-500'}`} size={isPWA ? 14 : 16} aria-hidden="true" />
                  {!isPWA && 'Filters'}
                  {getActiveFilters().length > 0 && (
                    <span className={`${isPWA ? 'ml-1' : 'ml-2'} inline-flex items-center justify-center ${isPWA ? 'px-1.5 py-0.5' : 'px-2 py-0.5'} rounded-full text-xs font-medium ${isPWA ? 'bg-gradient-to-r from-orange-500 to-amber-500 text-white shadow-sm' : 'bg-orange-600 text-white'}`}>
                      {getActiveFilters().length}
                    </span>
                  )}
                  {showFilters ? (
                    <FaChevronUp className={isPWA ? 'ml-1' : 'ml-1'} size={isPWA ? 12 : 14} aria-hidden="true" />
                  ) : (
                    <FaChevronDown className={isPWA ? 'ml-1' : 'ml-1'} size={isPWA ? 12 : 14} aria-hidden="true" />
                  )}
                </button>
              </div>
            </div>

            {/* Advanced Filters - Collapsible with smooth animation */}
            <div
              id="filters-panel"
              className={`${isPWA ? (showFilters ? 'max-h-[1000px] opacity-100' : 'max-h-0 opacity-0') : ''} transition-all duration-300 overflow-hidden`}
              role="region"
              aria-label={isConsultant ? "Client filters" : "Student filters"}
            >
              {showFilters && (
                <div className={`${isPWA ? 'bg-gradient-to-br from-orange-50/50 to-amber-50/30 border-2 border-orange-200/50' : 'bg-gray-50 border border-gray-200'} ${isPWA ? 'p-3 sm:p-4' : 'p-4'} rounded-lg ${isPWA ? 'shadow-md mt-2' : ''}`}>
                  <div className={`grid ${isPWA ? 'grid-cols-2 gap-2 sm:gap-3' : 'grid-cols-1 md:grid-cols-3 gap-4'}`}>
                    <div>
                      <label htmlFor="filter-program" className={`block ${isPWA ? 'text-xs sm:text-sm' : 'text-sm'} font-medium ${isPWA ? 'text-orange-800' : 'text-gray-700'} mb-1`}>
                        Program
                      </label>
                      <select
                        id="filter-program"
                        name="program"
                        className={`w-full ${isPWA ? 'px-2 py-1.5 text-xs sm:text-sm border-2' : 'px-3 py-2 border'} ${isPWA ? 'border-orange-200 focus:border-orange-400' : 'border-gray-300'} ${isPWA ? 'rounded-lg' : 'rounded-md'} shadow-sm focus:outline-none focus:ring-2 ${isPWA ? 'focus:ring-orange-400' : 'focus:ring-orange-500'} ${isPWA ? 'focus:border-orange-400' : 'focus:border-orange-500'} ${isPWA ? 'bg-white/80' : 'bg-white'}`}
                        value={filters.program}
                        onChange={handleFilterChange}
                        aria-label="Filter by program"
                      >
                        <option value="">All Programs</option>
                        {programs.map((prog) => (
                          <option key={prog._id} value={prog._id}>
                            {prog.name}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label htmlFor="filter-status" className={`block ${isPWA ? 'text-xs sm:text-sm' : 'text-sm'} font-medium ${isPWA ? 'text-orange-800' : 'text-gray-700'} mb-1`}>
                        Status
                      </label>
                      <select
                        id="filter-status"
                        name="status"
                        className={`w-full ${isPWA ? 'px-2 py-1.5 text-xs sm:text-sm border-2' : 'px-3 py-2 border'} ${isPWA ? 'border-orange-200 focus:border-orange-400' : 'border-gray-300'} ${isPWA ? 'rounded-lg' : 'rounded-md'} shadow-sm focus:outline-none focus:ring-2 ${isPWA ? 'focus:ring-orange-400' : 'focus:ring-orange-500'} ${isPWA ? 'focus:border-orange-400' : 'focus:border-orange-500'} ${isPWA ? 'bg-white/80' : 'bg-white'}`}
                        value={filters.status}
                        onChange={handleFilterChange}
                        aria-label="Filter by status"
                      >
                        <option value="">All Statuses</option>
                        <option value="active">Active</option>
                        <option value="pending">Pending</option>
                      </select>
                    </div>
                    <div>
                      <label htmlFor="filter-hasReview" className={`block ${isPWA ? 'text-xs sm:text-sm' : 'text-sm'} font-medium ${isPWA ? 'text-orange-800' : 'text-gray-700'} mb-1`}>
                        Has Review
                      </label>
                      <select
                        id="filter-hasReview"
                        name="hasReview"
                        className={`w-full ${isPWA ? 'px-2 py-1.5 text-xs sm:text-sm border-2' : 'px-3 py-2 border'} ${isPWA ? 'border-orange-200 focus:border-orange-400' : 'border-gray-300'} ${isPWA ? 'rounded-lg' : 'rounded-md'} shadow-sm focus:outline-none focus:ring-2 ${isPWA ? 'focus:ring-orange-400' : 'focus:ring-orange-500'} ${isPWA ? 'focus:border-orange-400' : 'focus:border-orange-500'} ${isPWA ? 'bg-white/80' : 'bg-white'}`}
                        value={filters.hasReview}
                        onChange={handleFilterChange}
                        aria-label="Filter by review status"
                      >
                        <option value="">{isConsultant ? 'All Clients' : 'All Students'}</option>
                        <option value="true">With Reviews</option>
                        <option value="false">Without Reviews</option>
                      </select>
                    </div>
                    <div>
                      <label htmlFor="filter-minProgress" className={`block ${isPWA ? 'text-xs sm:text-sm' : 'text-sm'} font-medium ${isPWA ? 'text-orange-800' : 'text-gray-700'} mb-1`}>
                        Min Progress (%)
                      </label>
                      <input
                        type="number"
                        id="filter-minProgress"
                        name="minProgress"
                        min="0"
                        max="100"
                        className={`w-full ${isPWA ? 'px-2 py-1.5 text-xs sm:text-sm border-2' : 'px-3 py-2 border'} ${isPWA ? 'border-orange-200 focus:border-orange-400' : 'border-gray-300'} ${isPWA ? 'rounded-lg' : 'rounded-md'} shadow-sm focus:outline-none focus:ring-2 ${isPWA ? 'focus:ring-orange-400' : 'focus:ring-orange-500'} ${isPWA ? 'focus:border-orange-400' : 'focus:border-orange-500'} ${isPWA ? 'bg-white/80' : 'bg-white'}`}
                        value={filters.minProgress}
                        onChange={handleFilterChange}
                        aria-label="Minimum progress percentage"
                      />
                    </div>
                    <div>
                      <label htmlFor="filter-maxProgress" className={`block ${isPWA ? 'text-xs sm:text-sm' : 'text-sm'} font-medium ${isPWA ? 'text-orange-800' : 'text-gray-700'} mb-1`}>
                        Max Progress (%)
                      </label>
                      <input
                        type="number"
                        id="filter-maxProgress"
                        name="maxProgress"
                        min="0"
                        max="100"
                        className={`w-full ${isPWA ? 'px-2 py-1.5 text-xs sm:text-sm border-2' : 'px-3 py-2 border'} ${isPWA ? 'border-orange-200 focus:border-orange-400' : 'border-gray-300'} ${isPWA ? 'rounded-lg' : 'rounded-md'} shadow-sm focus:outline-none focus:ring-2 ${isPWA ? 'focus:ring-orange-400' : 'focus:ring-orange-500'} ${isPWA ? 'focus:border-orange-400' : 'focus:border-orange-500'} ${isPWA ? 'bg-white/80' : 'bg-white'}`}
                        value={filters.maxProgress}
                        onChange={handleFilterChange}
                        aria-label="Maximum progress percentage"
                      />
                    </div>
                  </div>
                  {/* Reset Filters Button - Only show when filters are active */}
                  {(filters.program || filters.status || filters.hasReview || filters.minProgress || filters.maxProgress) && (
                    <div className={`mt-3 ${isPWA ? 'pt-3 border-t border-orange-200/50' : ''}`}>
                      <button
                        onClick={resetFilters}
                        aria-label="Reset all filters"
                        className={`w-full ${isPWA ? 'px-3 py-2 text-xs sm:text-sm bg-gradient-to-r from-orange-100 to-amber-100 hover:from-orange-200 hover:to-amber-200 text-orange-700 border-2 border-orange-200' : 'px-3 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700'} rounded-lg transition-all duration-200 ${isPWA ? 'font-medium shadow-sm' : ''}`}
                      >
                        Reset Filters
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Students Card Grid */}
        <div className="mb-6">
          {/* Active Filters and Result Count */}
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-4">
            {/* Active Filter Chips */}
            {getActiveFilters().length > 0 && (
              <div className="flex flex-wrap gap-2 items-center">
                <span className="text-sm text-gray-600 font-medium">Active filters:</span>
                {getActiveFilters().map((filter) => (
                  <span
                    key={filter.key}
                    className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${isPWA ? 'bg-gradient-to-r from-orange-100 to-amber-100 text-orange-800 shadow-sm' : 'bg-blue-100 text-blue-800'}`}
                  >
                    {filter.label}: {filter.displayValue}
                    <button
                      onClick={() => {
                        if (filter.key === 'search') {
                          setSearchTerm('');
                        } else {
                          setFilters(prev => ({ ...prev, [filter.key]: '' }));
                        }
                      }}
                      className={`ml-2 ${isPWA ? 'text-orange-600 hover:text-orange-800' : 'text-blue-600 hover:text-blue-800'} focus:outline-none focus:ring-2 ${isPWA ? 'focus:ring-orange-500' : 'focus:ring-blue-500'} rounded`}
                      aria-label={`Remove ${filter.label} filter`}
                    >
                      <FaTimes className="h-3 w-3" />
                    </button>
                  </span>
                ))}
                {getActiveFilters().length > 1 && (
                  <button
                    onClick={() => {
                      setSearchTerm('');
                      resetFilters();
                    }}
                    className={`text-sm ${isPWA ? 'text-orange-600 hover:text-orange-800' : 'text-blue-600 hover:text-blue-800'} font-medium`}
                  >
                    Clear All
                  </button>
                )}
              </div>
            )}

            {/* Result Count */}
            <div className="text-sm text-gray-600">
              {sortedStudents.length === 0 ? (
                <span>{isConsultant ? 'No clients found' : 'No students found'}</span>
              ) : itemsPerPage === 'all' ? (
                <span>{sortedStudents.length} {isConsultant ? (sortedStudents.length === 1 ? 'client' : 'clients') : (sortedStudents.length === 1 ? 'student' : 'students')}</span>
              ) : (
                <span>
                  Showing {Math.min((currentPage - 1) * itemsPerPage + 1, sortedStudents.length)}-{Math.min(currentPage * itemsPerPage, sortedStudents.length)} of {sortedStudents.length} {isConsultant ? (sortedStudents.length === 1 ? 'client' : 'clients') : (sortedStudents.length === 1 ? 'student' : 'students')}
                </span>
              )}
            </div>
          </div>

          {/* Sort Options */}
          <div className="flex justify-end mb-4">
            <div className="relative inline-flex items-center">
              <label className={`text-sm ${isPWA ? 'text-orange-700' : 'text-gray-600'} font-medium mr-3 flex items-center gap-1`}>
                <FaSort className={isPWA ? 'text-orange-500' : 'text-gray-500'} />
                Sort by:
              </label>
              <div className="relative">
                <select
                  onChange={(e) => {
                    const [key, direction] = e.target.value.split('-');
                    setSortConfig({ key, direction });
                  }}
                  value={`${sortConfig.key}-${sortConfig.direction}`}
                  className={`appearance-none bg-white border-2 ${isPWA ? 'border-orange-200 hover:border-orange-400 focus:border-orange-400 focus:ring-orange-400' : 'border-gray-300 hover:border-gray-400 focus:border-blue-500 focus:ring-blue-500'} rounded-lg px-4 py-2 pr-10 text-sm font-medium ${isPWA ? 'text-orange-700' : 'text-gray-700'} focus:outline-none focus:ring-2 cursor-pointer transition-all ${isPWA ? 'shadow-md hover:shadow-lg' : 'shadow-sm hover:shadow-md'}`}
                  title="Sort students by name or join date"
                  aria-label={isConsultant ? "Sort clients" : "Sort students"}
                >
                  <option value="name-ascending">Name (A → Z)</option>
                  <option value="name-descending">Name (Z → A)</option>
                  <option value="joinDate-ascending">Join Date (Oldest First)</option>
                  <option value="joinDate-descending">Join Date (Newest First)</option>
                </select>
                <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                  <FaChevronDown className={isPWA ? 'text-orange-500' : 'text-gray-500'} size={14} />
                </div>
              </div>
            </div>
          </div>

          {/* Cards Grid */}
          {isLoading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[...Array(6)].map((_, i) => (
                <SkeletonLoader key={i} />
              ))}
            </div>
          ) : paginatedStudents.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {paginatedStudents.map((student) => (
                <div
                  key={student.planId}
                  className={`${isPWA ? 'bg-gradient-to-br from-white to-orange-50/20 border-2' : 'bg-white border'} rounded-xl ${isPWA ? 'shadow-xl hover:shadow-2xl' : 'shadow-md hover:shadow-xl'} transition-all duration-300 overflow-hidden group relative ${student.status === 'pending'
                    ? isPWA ? 'border-yellow-300/50 shadow-yellow-100/50' : 'border-2 border-yellow-300 shadow-yellow-100'
                    : isPWA ? 'border-orange-200/50' : 'border border-gray-100'
                    }`}
                >
                  {isPWA && (
                    <>
                      <div className="absolute top-0 right-0 w-24 h-24 bg-gradient-to-br from-orange-100/20 to-amber-100/10 rounded-full blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                      <div className="absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r from-transparent via-orange-300/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                    </>
                  )}
                  {/* Card Header with Avatar and Status */}
                  <div className={`relative p-6 ${student.status === 'pending'
                    ? isPWA ? 'bg-gradient-to-br from-yellow-50/80 via-amber-50/80 to-orange-50/80' : 'bg-gradient-to-br from-yellow-50 via-amber-50 to-orange-50'
                    : isPWA ? 'bg-gradient-to-br from-orange-50/40 via-amber-50/30 to-yellow-50/20' : 'bg-gradient-to-br from-blue-50 to-indigo-50'
                    }`}>
                    {/* Pending Card Top Accent Stripe */}
                    {student.status === 'pending' && (
                      <div className={`absolute top-0 left-0 right-0 ${isPWA ? 'h-1' : 'h-1'} bg-gradient-to-r from-yellow-400 via-amber-400 to-orange-400`}></div>
                    )}
                    <div className={`flex items-start justify-between ${isPWA ? 'relative z-10' : ''}`}>
                      <div className="flex items-center space-x-4 flex-1">
                        <div className="relative">
                          <Avatar
                            src={getAvatarImage(student.image)}
                            name={student.name}
                            size={64}
                            className={`${isPWA ? 'border-4 border-white shadow-lg ring-2 ring-orange-200/50' : 'border-4 border-white shadow-md'}`}
                            alt={`${student.name} profile picture`}
                          />
                          <span className={`absolute bottom-0 right-0 h-5 w-5 rounded-full border-2 border-white ${isPWA ? 'shadow-md' : ''} ${student.status === 'active' ? 'bg-green-500' :
                            student.status === 'pending' ? 'bg-yellow-500' :
                              'bg-gray-400'
                            }`}></span>
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className={`${isPWA ? 'text-base sm:text-lg' : 'text-lg'} font-bold ${isPWA ? 'text-orange-900' : 'text-gray-900'} truncate mb-1`} id={`student-name-${student.planId}`}>
                            {formatName(student.name)}
                          </h3>
                          <div className={`space-y-1 ${isPWA ? 'space-y-0.5' : ''}`}>
                            <p className={`${isPWA ? 'text-xs sm:text-sm' : 'text-sm'} ${isPWA ? 'text-orange-700/80' : 'text-gray-600'} truncate flex items-center`} aria-label={`Email: ${student.email}`}>
                              {isPWA && <FaEnvelope className="mr-1.5 text-orange-500 text-xs flex-shrink-0" />}
                              <span className="truncate">{student.email}</span>
                            </p>
                            {student.phone && (
                              <p className={`${isPWA ? 'text-xs' : 'text-xs'} ${isPWA ? 'text-orange-600/70' : 'text-gray-500'} flex items-center`} aria-label={`Phone: ${student.phone.startsWith('temp_') ? 'Not specified' : student.phone}`}>
                                <FaPhone className={`${isPWA ? 'mr-1.5 text-orange-500' : 'mr-1'}`} aria-hidden="true" />
                                {student.phone.startsWith('temp_') ? 'Not specified' : student.phone}
                              </p>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Actions Menu */}
                      <div className="relative" data-action-menu>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setOpenActionMenu(openActionMenu === student.planId ? null : student.planId);
                            if (openActionMenu !== student.planId) {
                              actionMenuFocusedIndex.current[student.planId] = 0;
                            }
                          }}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              setOpenActionMenu(openActionMenu === student.planId ? null : student.planId);
                              if (openActionMenu !== student.planId) {
                                actionMenuFocusedIndex.current[student.planId] = 0;
                              }
                            }
                          }}
                          className={`p-2 md:p-2 p-3 rounded-full ${isPWA ? 'hover:bg-white/70 hover:shadow-md' : 'hover:bg-white/50'} transition-all ${isPWA ? 'focus:ring-orange-500' : 'focus:ring-blue-500'} focus:outline-none focus:ring-2 focus:ring-offset-2`}
                          aria-label={`Actions menu for ${student.name}`}
                          aria-expanded={openActionMenu === student.planId}
                          aria-haspopup="true"
                          title="View actions for this student (approve, reject, send reminder, view history, etc.)"
                        >
                          <FaEllipsisV className="text-gray-600" aria-hidden="true" />
                        </button>

                        {openActionMenu === student.planId && (
                          <div
                            className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 z-10 py-1"
                            data-action-dropdown
                            role="menu"
                            aria-label={`Actions for ${student.name}`}
                            onKeyDown={(e) => {
                              const menuItems = student.status === 'pending'
                                ? ['approve', 'reject']
                                : [
                                  ...((student.payment?.status === 'pending' || student.payment?.status === 'partially_paid' || (student.payment?.dueAmount || 0) > 0) ? ['reminder'] : []),
                                  ...(student.payment && student.payment.totalAmount > 0 ? ['plan'] : []),
                                  'message',
                                  'history',
                                  'remove'
                                ].filter(Boolean);
                              handleActionMenuKeyDown(e, student.planId, menuItems);
                            }}
                          >
                            {student.status === 'pending' ? (
                              <>
                                {!student.isFromAppointment ? (
                                  <>
                                    <button
                                      ref={(el) => {
                                        if (el) actionMenuRefs.current[`${student.planId}-0`] = el;
                                      }}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleApprove(student.planId);
                                        setOpenActionMenu(null);
                                      }}
                                      className="w-full text-left px-4 py-2 text-sm text-green-600 hover:bg-green-50 flex items-center focus:outline-none focus:bg-green-50"
                                      role="menuitem"
                                      aria-label={`Approve ${student.name}`}
                                      tabIndex={0}
                                    >
                                      <FaUserCheck className="mr-2" aria-hidden="true" />
                                      Approve
                                    </button>
                                    <button
                                      ref={(el) => {
                                        if (el) actionMenuRefs.current[`${student.planId}-1`] = el;
                                      }}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleReject(student.planId);
                                        setOpenActionMenu(null);
                                      }}
                                      className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 flex items-center focus:outline-none focus:bg-red-50"
                                      role="menuitem"
                                      aria-label={`Reject ${student.name}`}
                                      tabIndex={0}
                                    >
                                      <FaTimes className="mr-2" aria-hidden="true" />
                                      Reject
                                    </button>
                                  </>
                                ) : (
                                  <div className="px-4 py-2 text-xs text-gray-500 italic">
                                    Manage via Appointments
                                  </div>
                                )}
                              </>
                            ) : (
                              <>
                                {!student.isFromAppointment && (student.payment?.status === 'pending' || student.payment?.status === 'partially_paid' || (student.payment?.dueAmount || 0) > 0) && (
                                  <button
                                    ref={(el) => {
                                      const index = 0;
                                      if (el) actionMenuRefs.current[`${student.planId}-${index}`] = el;
                                    }}
                                    onClick={async (e) => {
                                      e.stopPropagation();
                                      await handleSendReminder(student.planId);
                                      setOpenActionMenu(null);
                                    }}
                                    disabled={sendingReminder === student.planId}
                                    className="w-full text-left px-4 py-2 text-sm text-orange-600 hover:bg-orange-50 flex items-center disabled:opacity-50 focus:outline-none focus:bg-orange-50"
                                    role="menuitem"
                                    aria-label={`Send payment reminder to ${student.name}`}
                                    tabIndex={0}
                                  >
                                    <FaEnvelope className="mr-2" aria-hidden="true" />
                                    {sendingReminder === student.planId ? 'Sending...' : 'Send Reminder'}
                                  </button>
                                )}
                                {(() => {
                                  // Check if there are any payment plans available for this student's program
                                  const studentProgramId = student.programId?.toString() || student.programDetails?.programId?.toString();
                                  const hasAvailablePlans = paymentPlans.some(plan => {
                                    const planProgramId = plan.programId?._id?.toString() || plan.programId?.toString();
                                    return planProgramId && studentProgramId && planProgramId === studentProgramId && plan.isActive !== false;
                                  });

                                  // Only show button if plans are available and student has payment amount
                                  if (!hasAvailablePlans || !student.payment || student.payment.totalAmount === 0) {
                                    return null;
                                  }

                                  return (
                                    <button
                                      ref={(el) => {
                                        const index = (student.payment?.status === 'pending' || student.payment?.status === 'partially_paid' || (student.payment?.dueAmount || 0) > 0) ? 1 : 0;
                                        if (el) actionMenuRefs.current[`${student.planId}-${index}`] = el;
                                      }}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setShowPlanAssignment(student.planId);
                                        setOpenActionMenu(null);
                                      }}
                                      className="w-full text-left px-4 py-2 text-sm text-blue-600 hover:bg-blue-50 flex items-center focus:outline-none focus:bg-blue-50"
                                      role="menuitem"
                                      aria-label={`${student.paymentPlanId ? 'Change' : 'Assign'} payment plan for ${student.name}`}
                                      tabIndex={0}
                                    >
                                      <FaCreditCard className="mr-2" aria-hidden="true" />
                                      {student.paymentPlanId ? 'Change Payment Plan' : 'Assign Payment Plan'}
                                    </button>
                                  );
                                })()}
                                {student?._id && (
                                  <button
                                    ref={(el) => {
                                      let index = 0;
                                      if ((student.payment?.status === 'pending' || student.payment?.status === 'partially_paid' || (student.payment?.dueAmount || 0) > 0)) index++;
                                      if (student.payment && student.payment.totalAmount > 0) index++;
                                      if (el) actionMenuRefs.current[`${student.planId}-${index}`] = el;
                                    }}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setShowCommunicationHistory(student._id);
                                      setOpenActionMenu(null);
                                    }}
                                    className="w-full text-left px-4 py-2 text-sm text-purple-600 hover:bg-purple-50 flex items-center focus:outline-none focus:bg-purple-50"
                                    role="menuitem"
                                    aria-label={`View communication history with ${student.name}`}
                                    tabIndex={0}
                                  >
                                    <FaHistory className="mr-2" aria-hidden="true" />
                                    View History
                                  </button>
                                )}
                                <button
                                  ref={(el) => {
                                    let index = 0;
                                    if ((student.payment?.status === 'pending' || student.payment?.status === 'partially_paid' || (student.payment?.dueAmount || 0) > 0)) index++;
                                    if (student.payment && student.payment.totalAmount > 0) index++;
                                    if (student?.studentId) index++;
                                    if (el) actionMenuRefs.current[`${student.planId}-${index}`] = el;
                                  }}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    if (student?.studentId) {
                                      setMessageStudentId(student.studentId);
                                      setMessageContent('');
                                      setShowMessageModal(true);
                                    } else {
                                      toast.error(isConsultant ? 'Client ID not found' : 'Student ID not found');
                                    }
                                    setOpenActionMenu(null);
                                  }}
                                  className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center focus:outline-none focus:bg-gray-50"
                                  role="menuitem"
                                  aria-label={`Send message to ${student.name}`}
                                  tabIndex={0}
                                >
                                  <FaEnvelope className="mr-2" aria-hidden="true" />
                                  Send Message
                                </button>
                                {!student.isFromAppointment && (
                                  <button
                                    ref={(el) => {
                                      let index = 0;
                                      if ((student.payment?.status === 'pending' || student.payment?.status === 'partially_paid' || (student.payment?.dueAmount || 0) > 0)) index++;
                                      if (student.payment && student.payment.totalAmount > 0) index++;
                                      if (student?.studentId) index++;
                                      index++;
                                      if (el) actionMenuRefs.current[`${student.planId}-last`] = el;
                                    }}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleRemoveStudent(student.planId, student.name);
                                      setOpenActionMenu(null);
                                    }}
                                    className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 flex items-center focus:outline-none focus:bg-red-50"
                                    role="menuitem"
                                    aria-label={`Remove ${student.name} from students`}
                                    tabIndex={0}
                                  >
                                    <FaTrash className="mr-2" aria-hidden="true" />
                                    Remove Client
                                  </button>
                                )}
                              </>
                            )}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Status Badge */}
                    <div className={`${isPWA ? 'mt-3' : 'mt-4'} ${isPWA ? 'relative z-10' : ''}`}>
                      <span
                        className={`inline-flex items-center ${isPWA ? 'px-3 py-1.5' : 'px-3 py-1'} rounded-full ${isPWA ? 'text-xs sm:text-sm' : 'text-xs'} font-bold ${isPWA ? 'shadow-md' : ''} ${student.status === 'active'
                          ? isPWA ? 'bg-gradient-to-r from-green-500 to-emerald-600 text-white border-2 border-green-400/50 shadow-lg' : 'bg-green-100 text-green-800'
                          : student.status === 'pending'
                            ? isPWA ? 'bg-gradient-to-r from-yellow-500 to-amber-600 text-white border-2 border-yellow-400/50 shadow-lg' : 'bg-yellow-100 text-yellow-800'
                            : isPWA ? 'bg-gradient-to-r from-gray-400 to-slate-500 text-white border-2 border-gray-300/50 shadow-lg' : 'bg-gray-100 text-gray-800'
                          }`}
                        role="status"
                        aria-label={`${isConsultant ? 'Client' : 'Student'} status: ${student.status === 'active' ? (isConsultant ? 'Active - Confirmed' : 'Active - Approved and enrolled') : student.status === 'pending' ? 'Pending - Awaiting approval' : student.status}`}
                        title={student.status === 'active' ? (isConsultant ? 'Active: Confirmed' : 'Active: Approved and enrolled') : student.status === 'pending' ? 'Pending: Awaiting approval' : `Status: ${student.status}`}
                      >
                        {student.status === 'active' && <FaCheckCircle className={`${isPWA ? 'mr-1.5' : 'mr-1'}`} aria-hidden="true" />}
                        {student.status === 'pending' && isPWA && <FaClock className="mr-1.5" aria-hidden="true" />}
                        <span className="uppercase tracking-wide">{student.status}</span>
                      </span>
                    </div>
                  </div>

                  {/* Card Body */}
                  <div className={`${isPWA ? 'p-4 sm:p-5' : 'p-4 md:p-6'} ${isPWA ? 'space-y-3 sm:space-y-4 bg-white/60' : 'space-y-4'}`}>
                    {/* Program Info */}
                    <div className={`${isPWA ? 'bg-gradient-to-br from-orange-50/50 to-amber-50/30 border-2 border-orange-200/50 rounded-lg p-3 sm:p-4' : ''} border-b ${isPWA ? 'border-0' : 'border-gray-100'} ${isPWA ? 'pb-0' : 'pb-4'}`} role="region" aria-labelledby={`program-${student.planId}`}>
                      <div className={`flex items-center justify-between ${isPWA ? 'mb-2' : 'mb-2'}`}>
                        <span className={`${isPWA ? 'text-[10px] sm:text-xs' : 'text-xs'} font-bold ${isPWA ? 'text-orange-700 uppercase tracking-wide' : 'text-gray-500 uppercase'} flex items-center gap-1.5`} id={`program-${student.planId}`}>
                          {isPWA && <FaBook className="text-orange-600" />}
                          {isConsultant ? 'Service' : 'Program'}
                        </span>
                        <span className={`${isPWA ? 'text-xs sm:text-sm' : 'text-xs'} font-bold ${isPWA ? 'text-orange-600 bg-orange-100/50 px-2 py-0.5 rounded' : 'text-blue-600'}`} aria-label={`${isConsultant ? 'Service' : 'Program'} price: ${formatCurrency(student.programDetails?.price || 0)}`}>
                          {formatCurrency(student.programDetails?.price || 0)}
                        </span>
                      </div>
                      <p className={`${isPWA ? 'text-sm sm:text-base' : 'text-sm'} font-bold ${isPWA ? 'text-orange-900' : 'text-gray-900'} mt-1`} aria-label={`${isConsultant ? 'Service' : 'Program'} name: ${student.programDetails?.name || 'N/A'}`}>
                        {student.programDetails?.name || 'N/A'}
                      </p>
                      {/* Show base price and discount if applicable */}
                      {student.programDetails?.basePrice && student.programDetails?.basePrice > student.programDetails?.price && (
                        <div className="flex items-center gap-2 mt-2 text-xs">
                          <span className="text-gray-400 line-through">
                            {formatCurrency(student.programDetails.basePrice)}
                          </span>
                          {student.programDetails?.discountApplied > 0 && (
                            <span className="text-green-600 font-medium flex items-center">
                              <FaPercent className="mr-0.5" />
                              {student.programDetails.discountApplied}% off
                            </span>
                          )}
                        </div>
                      )}
                      {/* Show scholarship if applicable */}
                      {student.scholarship && (
                        <div className="flex items-center gap-1 mt-2 text-xs text-purple-600 font-medium">
                          <FaGraduationCap className="text-sm" />
                          <span>{student.scholarship.name}</span>
                          {student.scholarship.discountPercentage > 0 && (
                            <span>({student.scholarship.discountPercentage}%)</span>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Payment Information - Show for pending/active students if program has price or payment info exists */}
                    {student.payment && (
                      (student.status === 'pending' && (student.programDetails?.price > 0 || student.payment.totalAmount > 0)) ||
                      (student.status === 'active' && (student.programDetails?.price > 0 || student.payment.totalAmount > 0 || student.payment.paidAmount > 0))
                    ) && (
                        <div className="border-b border-gray-100 pb-4" role="region" aria-labelledby={`payment-${student.planId}`}>
                          <div className="flex items-center justify-between mb-3">
                            <span className="text-xs font-medium text-gray-500 uppercase flex items-center" id={`payment-${student.planId}`}>
                              <FaWallet className="mr-1" aria-hidden="true" />
                              Payment
                              <FaInfoCircle
                                className="ml-1.5 text-gray-400 hover:text-gray-600 cursor-help text-xs"
                                title="View payment details and history. Click 'View History' to see all transactions."
                                aria-label="Payment help: View payment details and history"
                              />
                            </span>
                            <span
                              className={`text-xs font-semibold px-2 py-1 rounded-full ${getPaymentStatusColor(student.payment.status)}`}
                              role="status"
                              aria-label={`Payment status: ${formatPaymentStatus(student.payment.status)}`}
                            >
                              {formatPaymentStatus(student.payment.status)}
                            </span>
                          </div>
                          <div className="space-y-2 text-xs">
                            <div className="flex items-center justify-between">
                              <span className="text-gray-600 flex items-center">
                                <FaCreditCard className="mr-1.5 text-gray-400" />
                                Total:
                              </span>
                              <span className="font-semibold text-gray-900">
                                {formatCurrency((() => {
                                  // For pending/active students, use program price if payment.totalAmount is 0
                                  if (student.payment.totalAmount === 0 && student.programDetails?.price > 0) {
                                    return student.programDetails.price;
                                  }
                                  return student.payment.totalAmount || 0;
                                })())}
                              </span>
                            </div>
                            <div className="flex items-center justify-between">
                              <span className="text-gray-600 flex items-center">
                                <FaCheckCircle className="mr-1.5 text-green-500" />
                                Paid:
                              </span>
                              <span className="font-semibold text-green-600">
                                {formatCurrency(student.payment.paidAmount || 0)}
                              </span>
                            </div>
                            <div className="flex items-center justify-between">
                              <span className="text-gray-600 flex items-center">
                                <FaMoneyBillWave className="mr-1.5 text-orange-500" />
                                Remaining:
                              </span>
                              <span className={`font-semibold ${(() => {
                                // For pending/active students, use program price if payment.totalAmount is 0
                                const remaining = (student.payment.totalAmount === 0 && student.programDetails?.price > 0)
                                  ? (student.programDetails.price - (student.payment.paidAmount || 0))
                                  : (student.payment.dueAmount || 0);
                                return remaining > 0 ? 'text-orange-600' : 'text-green-600';
                              })()
                                }`}>
                                {formatCurrency((() => {
                                  // For pending/active students, use program price if payment.totalAmount is 0
                                  if (student.payment.totalAmount === 0 && student.programDetails?.price > 0) {
                                    return student.programDetails.price - (student.payment.paidAmount || 0);
                                  }
                                  return student.payment.dueAmount || 0;
                                })())}
                              </span>
                            </div>
                            {student.paymentPlanId && (
                              <div className="mt-2 pt-2 border-t border-gray-100">
                                <div className="flex items-center justify-between">
                                  <div>
                                    <span className="text-blue-600 text-xs font-medium flex items-center">
                                      <FaCreditCard className="mr-1" />
                                      Installment Plan Active
                                    </span>
                                    {student.paymentPlanName && (
                                      <p className="text-xs text-gray-600 mt-1">{student.paymentPlanName}</p>
                                    )}
                                  </div>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setShowPlanAssignment(student.planId);
                                    }}
                                    className="text-xs text-blue-600 hover:text-blue-800 font-medium"
                                  >
                                    View/Change
                                  </button>
                                </div>
                              </div>
                            )}
                            {/* Assign Payment Plan - Only for active students with payment amount AND available plans */}
                            {(() => {
                              // Check if there are any payment plans available for this student's program
                              const studentProgramId = student.programId?.toString() || student.programDetails?.programId?.toString();
                              const hasAvailablePlans = paymentPlans.some(plan => {
                                const planProgramId = plan.programId?._id?.toString() || plan.programId?.toString();
                                return planProgramId && studentProgramId && planProgramId === studentProgramId && plan.isActive !== false;
                              });

                              // Only show button if plans are available, student is active, has no plan, and has payment amount
                              if (student.status !== 'active' || student.paymentPlanId || !student.payment || student.payment.totalAmount === 0 || !hasAvailablePlans) {
                                return null;
                              }

                              return (
                                <div className="mt-2 pt-2 border-t border-gray-100">
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setShowPlanAssignment(student.planId);
                                    }}
                                    className="text-xs text-blue-600 hover:text-blue-800 font-medium flex items-center gap-1"
                                  >
                                    <FaCreditCard className="text-xs" />
                                    Assign Payment Plan
                                  </button>
                                </div>
                              );
                            })()}
                            {/* Message for pending students */}
                            {student.status === 'pending' && (
                              <div className="mt-2 pt-2 border-t border-gray-100">
                                <span className="text-xs text-gray-500 italic">
                                  Payment plan can be assigned after approval
                                </span>
                              </div>
                            )}
                            {/* Payment Overdue - Only show for active students with due amount */}
                            {student.status === 'active' && (student.payment.dueAmount || 0) > 0 && (
                              <div className="mt-2 pt-2 border-t border-gray-100">
                                <span className="text-red-600 text-xs font-medium flex items-center">
                                  <FaExclamationTriangle className="mr-1" />
                                  Payment Overdue
                                </span>
                              </div>
                            )}
                            {/* View History - Only show for active students with payment amount */}
                            {student.status === 'active' && student.payment.totalAmount > 0 && (
                              <div className="mt-3 pt-2 border-t border-gray-100">
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setShowPaymentHistory(student.planId);
                                  }}
                                  className="text-xs text-blue-600 hover:text-blue-800 font-medium flex items-center gap-1"
                                >
                                  <FaHistory className="text-xs" />
                                  View History
                                </button>
                              </div>
                            )}
                            {/* Message for active students with 0 amount */}
                            {student.status === 'active' && student.payment.totalAmount === 0 && (
                              <div className="mt-3 pt-2 border-t border-gray-100">
                                <span className="text-xs text-gray-500 italic">
                                  No payment information available
                                </span>
                              </div>
                            )}
                          </div>
                        </div>
                      )}

                    {/* Progress */}
                    <div className={`${isPWA ? 'bg-gradient-to-br from-blue-50/40 to-indigo-50/30 border-2 border-blue-200/50 rounded-lg p-3 sm:p-4' : ''} border-b ${isPWA ? 'border-0' : 'border-gray-100'} ${isPWA ? 'pb-0' : 'pb-4'}`} role="region" aria-labelledby={`progress-${student.planId}`}>
                      <div className={`flex items-center justify-between ${isPWA ? 'mb-2' : 'mb-2'}`}>
                        <span className={`${isPWA ? 'text-[10px] sm:text-xs' : 'text-xs'} font-bold ${isPWA ? 'text-blue-700 uppercase tracking-wide' : 'text-gray-500 uppercase'} flex items-center gap-1.5`} id={`progress-${student.planId}`}>
                          {isPWA && <FaGraduationCap className="text-blue-600" />}
                          Progress
                          {!isPWA && (
                            <FaInfoCircle
                              className="ml-1.5 text-gray-400 hover:text-gray-600 cursor-help text-xs"
                              title={`Click the progress bar to view detailed ${isConsultant ? 'appointment' : 'progress'} breakdown by ${isConsultant ? 'service' : 'class'} type and ${isConsultant ? 'session' : 'attendance'} records`}
                              aria-label={`Progress help: Click to view detailed ${isConsultant ? 'appointment' : 'progress'} breakdown`}
                            />
                          )}
                        </span>
                        <span className={`${isPWA ? 'text-base sm:text-lg' : 'text-sm'} font-bold ${isPWA ? 'text-blue-600 bg-blue-100/50 px-2 py-0.5 rounded' : 'text-gray-900'}`} aria-label={`Progress: ${student.progress || 0} percent`}>
                          {student.progress || 0}%
                        </span>
                      </div>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setShowProgressBreakdown(student.planId);
                        }}
                        className={`w-full cursor-pointer focus:outline-none focus:ring-2 ${isPWA ? 'focus:ring-blue-500' : 'focus:ring-blue-500'} focus:ring-offset-2 rounded ${isPWA ? 'mt-2' : ''}`}
                        aria-label={`View detailed progress for ${student.name}. Current progress: ${student.progress || 0} percent`}
                      >
                        <ProgressBar progress={student.progress} isPWA={isPWA} />
                      </button>
                      <div className={`grid grid-cols-3 gap-2 ${isPWA ? 'mt-3' : 'mt-3'} ${isPWA ? 'text-xs sm:text-sm' : 'text-xs'} ${isPWA ? 'text-blue-700/80' : 'text-gray-600'}`} role="group" aria-label={isConsultant ? "Appointment statistics" : "Session statistics"}>
                        <div className={`${isPWA ? 'bg-white/60 rounded px-2 py-1 text-center' : ''}`}>
                          <div className={`${isPWA ? 'text-[10px] text-blue-600/70 mb-0.5' : 'font-medium'}`}>{isPWA ? 'Total' : 'Total:'}</div>
                          <div className={`${isPWA ? 'font-bold text-blue-900' : 'font-medium'}`} aria-label={`Total ${isConsultant ? 'appointments' : 'sessions'}: ${student.totalSessions || 0}`}>{student.totalSessions || 0}</div>
                        </div>
                        <div className={`${isPWA ? 'bg-white/60 rounded px-2 py-1 text-center' : ''}`}>
                          <div className={`${isPWA ? 'text-[10px] text-blue-600/70 mb-0.5' : 'font-medium'}`}>{isConsultant ? 'Completed' : (isPWA ? 'Attended' : 'Attended:')}</div>
                          <div className={`${isPWA ? 'font-bold text-blue-900' : 'font-medium'}`} aria-label={`${isConsultant ? 'Completed appointments' : 'Attended sessions'}: ${student.attendedSessions || 0}`}>{student.attendedSessions || 0}</div>
                        </div>
                        <div className={`${isPWA ? 'bg-white/60 rounded px-2 py-1 text-center' : ''}`}>
                          <div className={`${isPWA ? 'text-[10px] text-blue-600/70 mb-0.5' : 'font-medium'}`}>{isConsultant ? 'Completion' : (isPWA ? 'Rate' : 'Rate:')}</div>
                          <div className={`${isPWA ? 'font-bold text-blue-900' : 'font-medium'}`} aria-label={`${isConsultant ? 'Completion rate' : 'Attendance rate'}: ${student.progress || 0} percent`}>{student.progress || 0}%</div>
                        </div>
                      </div>
                      <div className={`flex flex-col sm:flex-row gap-2 ${isPWA ? 'mt-3' : 'mt-2'}`}>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setShowProgressBreakdown(student.planId);
                          }}
                          className={`${isPWA ? 'text-xs sm:text-sm bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 text-white shadow-md hover:shadow-lg border border-orange-400/50' : 'text-xs text-blue-600 hover:text-blue-800'} font-medium focus:outline-none focus:ring-2 ${isPWA ? 'focus:ring-orange-500' : 'focus:ring-blue-500'} focus:ring-offset-1 rounded-lg ${isPWA ? 'px-3 py-2' : 'px-2 py-1'} min-h-[44px] sm:min-h-0 transition-all`}
                          aria-label={`View detailed ${isConsultant ? 'appointment' : 'progress'} breakdown for ${student.name}`}
                          title={`View detailed ${isConsultant ? 'appointment history' : 'progress breakdown'} including ${isConsultant ? 'service' : 'class'} type and ${isConsultant ? 'session' : 'attendance'} records`}
                        >
                          {isPWA ? (isConsultant ? 'View History' : 'View Progress') : (isConsultant ? 'View History →' : 'View Progress →')}
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setShowAttendanceCalendar(student.planId);
                          }}
                          className={`${isPWA ? 'text-xs sm:text-sm bg-gradient-to-r from-emerald-500 to-green-500 hover:from-emerald-600 hover:to-green-600 text-white shadow-md hover:shadow-lg border border-emerald-400/50' : 'text-xs text-green-600 hover:text-green-800'} font-medium focus:outline-none focus:ring-2 ${isPWA ? 'focus:ring-emerald-500' : 'focus:ring-green-500'} focus:ring-offset-1 rounded-lg ${isPWA ? 'px-3 py-2' : 'px-2 py-1'} min-h-[44px] sm:min-h-0 transition-all`}
                          aria-label={`View ${isConsultant ? 'appointment' : 'attendance'} calendar for ${student.name}`}
                          title={`View ${isConsultant ? 'appointment' : 'attendance'} calendar with color-coded sessions`}
                        >
                          {isPWA ? (isConsultant ? 'View Schedule' : 'View Attendance') : (isConsultant ? 'View Schedule →' : 'View Attendance →')}
                        </button>
                      </div>
                    </div>

                    {/* Review Section - Show for all {isConsultant ? 'clients' : 'students'} */}
                    <div className={`${isPWA ? 'bg-gradient-to-br from-purple-50/40 to-pink-50/30 border-2 border-purple-200/50 rounded-lg p-3 sm:p-4' : ''} border-b ${isPWA ? 'border-0' : 'border-gray-100'} ${isPWA ? 'pb-0' : 'pb-4'}`}>
                      <span className={`${isPWA ? 'text-[10px] sm:text-xs' : 'text-xs'} font-bold ${isPWA ? 'text-purple-700 uppercase tracking-wide' : 'text-gray-500 uppercase'} block ${isPWA ? 'mb-2 flex items-center gap-1.5' : 'mb-2'}`}>
                        {isPWA && <FaStar className="text-purple-600" />}
                        Review
                      </span>
                      {student.status === 'pending' ? (
                        <div className={`${isPWA ? 'bg-yellow-50/50 border border-yellow-200/50 rounded-lg p-2 sm:p-3' : ''}`}>
                          <span className={`${isPWA ? 'text-xs sm:text-sm' : 'text-sm'} ${isPWA ? 'text-yellow-700 font-medium' : 'text-gray-500 italic'} flex items-center gap-1.5`}>
                            {isPWA && <FaClock className="text-yellow-600" />}
                            Approval is pending
                          </span>
                          {student.joinDate && (
                            <p className={`${isPWA ? 'text-[10px] sm:text-xs mt-1.5' : 'text-xs mt-1'} ${isPWA ? 'text-yellow-600/70' : 'text-gray-400'}`}>
                              Submitted {formatDateUtil(student.joinDate, dateFormat)}
                            </p>
                          )}
                        </div>
                      ) : student.review && typeof student.review.rating === 'number' && student.review.rating >= 1 && student.review.rating <= 5 ? (
                        <div className={`${isPWA ? 'bg-white/60 border border-purple-200/50 rounded-lg p-2 sm:p-3' : ''}`}>
                          <div className={`flex items-center ${isPWA ? 'justify-between mb-2' : 'justify-between'}`}>
                            <div className="flex items-center space-x-2">
                              {renderStars(student.review.rating)}
                              {isPWA && (
                                <span className={`text-xs sm:text-sm font-bold ${isPWA ? 'text-purple-700' : ''}`}>
                                  {student.review.rating}/5
                                </span>
                              )}
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  toggleReviewVisibility(student.planId);
                                }}
                                disabled={trainerSubscriptionPlan !== 'advance' || !advanceFeatures?.showHideTestimonials}
                                className={`${isPWA ? 'ml-2' : ''} ${trainerSubscriptionPlan === 'advance' && advanceFeatures?.showHideTestimonials
                                  ? isPWA ? 'text-purple-600 hover:text-purple-800 cursor-pointer' : 'text-gray-500 hover:text-blue-600 cursor-pointer'
                                  : 'text-gray-300 cursor-not-allowed opacity-50'
                                  }`}
                                title={
                                  trainerSubscriptionPlan === 'advance' && advanceFeatures?.showHideTestimonials
                                    ? "Show or hide this review on your public profile"
                                    : "This feature requires an Advance subscription plan"
                                }
                                aria-label={
                                  trainerSubscriptionPlan !== 'advance'
                                    ? 'Upgrade to advance plan to hide/show testimonials'
                                    : !advanceFeatures?.showHideTestimonials
                                      ? 'Upgrade to advance plan to hide/show testimonials'
                                      : (student.review.visible ? 'Hide from testimonials' : 'Show in testimonials')
                                }
                              >
                                {(trainerSubscriptionPlan === 'free' || student.review.visible) ? (
                                  <FaRegEye className={isPWA ? 'text-sm' : 'text-sm'} />
                                ) : (
                                  <FaRegEyeSlash className={isPWA ? 'text-sm' : 'text-sm'} />
                                )}
                              </button>
                            </div>
                            <span className={`${isPWA ? 'text-xs sm:text-sm font-medium' : 'text-xs'} ${isPWA ? 'text-purple-600 bg-purple-100/50 px-2 py-0.5 rounded' : 'text-gray-500'}`}>
                              ({trainerSubscriptionPlan === 'free' ? 'Visible' : (student.review.visible ? 'Visible' : 'Hidden')})
                            </span>
                          </div>
                        </div>
                      ) : (
                        <div className={`${isPWA ? 'bg-gray-50/50 border border-gray-200/50 rounded-lg p-2 sm:p-3' : ''}`}>
                          <span className={`${isPWA ? 'text-xs sm:text-sm' : 'text-sm'} ${isPWA ? 'text-gray-600 font-medium' : 'text-gray-400 italic'} flex items-center gap-1.5`}>
                            {isPWA && <FaStar className="text-gray-400" />}
                            No review yet
                          </span>
                        </div>
                      )}
                    </div>

                    {/* Join/Submission Date */}
                    <div className={`flex items-center justify-between ${isPWA ? 'text-xs sm:text-sm bg-gradient-to-r from-gray-50/50 to-slate-50/30 border border-gray-200/50 rounded-lg p-2 sm:p-3 mt-2' : 'text-sm'}`}>
                      <span className={`${isPWA ? 'text-gray-700 font-medium' : 'text-gray-500'} flex items-center gap-1.5`}>
                        <FaCalendarAlt className={isPWA ? 'text-gray-500' : 'text-gray-400'} />
                        {student.status === 'pending' ? 'Submitted' : 'Joined'}
                      </span>
                      <span className={`${isPWA ? 'font-bold text-gray-900' : 'font-medium text-gray-900'}`}>
                        {formatDate(student.joinDate)}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            renderEmptyState()
          )}

          {/* Pagination Controls */}
          {sortedStudents.length > 0 && itemsPerPage !== 'all' && totalPages > 1 && (
            <div className="mt-6 flex flex-col sm:flex-row justify-between items-center gap-4">
              {/* Items per page selector */}
              <div className="flex items-center gap-2">
                <label htmlFor="items-per-page" className="text-sm text-gray-600">
                  Show:
                </label>
                <select
                  id="items-per-page"
                  value={itemsPerPage}
                  onChange={(e) => {
                    const value = e.target.value === 'all' ? 'all' : parseInt(e.target.value, 10);
                    setItemsPerPage(value);
                    setCurrentPage(1);
                  }}
                  className="px-3 py-1.5 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="12">12</option>
                  <option value="24">24</option>
                  <option value="48">48</option>
                  <option value="all">All</option>
                </select>
              </div>

              {/* Page numbers */}
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                  disabled={currentPage === 1}
                  className="px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-blue-500"
                  aria-label="Previous page"
                >
                  <FaChevronUp className="rotate-[-90deg]" />
                </button>

                {/* Page number buttons */}
                <div className="flex gap-1">
                  {(() => {
                    const pages = [];
                    const maxVisible = 7;
                    let startPage = Math.max(1, currentPage - Math.floor(maxVisible / 2));
                    let endPage = Math.min(totalPages, startPage + maxVisible - 1);

                    if (endPage - startPage < maxVisible - 1) {
                      startPage = Math.max(1, endPage - maxVisible + 1);
                    }

                    if (startPage > 1) {
                      pages.push(
                        <button
                          key={1}
                          onClick={() => setCurrentPage(1)}
                          className="px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                          1
                        </button>
                      );
                      if (startPage > 2) {
                        pages.push(
                          <span key="ellipsis-start" className="px-2 py-2 text-gray-500">
                            ...
                          </span>
                        );
                      }
                    }

                    for (let i = startPage; i <= endPage; i++) {
                      pages.push(
                        <button
                          key={i}
                          onClick={() => setCurrentPage(i)}
                          className={`px-3 py-2 border rounded-md text-sm font-medium focus:outline-none focus:ring-2 focus:ring-blue-500 ${i === currentPage
                            ? 'bg-blue-600 text-white border-blue-600'
                            : 'border-gray-300 text-gray-700 bg-white hover:bg-gray-50'
                            }`}
                        >
                          {i}
                        </button>
                      );
                    }

                    if (endPage < totalPages) {
                      if (endPage < totalPages - 1) {
                        pages.push(
                          <span key="ellipsis-end" className="px-2 py-2 text-gray-500">
                            ...
                          </span>
                        );
                      }
                      pages.push(
                        <button
                          key={totalPages}
                          onClick={() => setCurrentPage(totalPages)}
                          className="px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                          {totalPages}
                        </button>
                      );
                    }

                    return pages;
                  })()}
                </div>

                <button
                  onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                  disabled={currentPage === totalPages}
                  className="px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-blue-500"
                  aria-label="Next page"
                >
                  <FaChevronUp className="rotate-90" />
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Add {isConsultant ? 'Client' : 'Student'} Modal */}
        {showAddModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-medium text-gray-900">{isConsultant ? 'Add New Client' : 'Add New Student'}</h3>
                  <button
                    onClick={() => setShowAddModal(false)}
                    className="text-gray-400 hover:text-gray-500"
                  >
                    &times;
                  </button>
                </div>
                <form onSubmit={handleAddStudent}>
                  <div className="mb-4">
                    <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                      Full Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      id="name"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      placeholder={isConsultant ? "Enter client's full name" : "Enter student's full name"}
                      aria-describedby="name-help"
                      value={newStudent.name}
                      onChange={(e) => setNewStudent({ ...newStudent, name: e.target.value })}
                      required
                    />
                    <p id="name-help" className="mt-1 text-xs text-gray-500">{isConsultant ? "Client's full name as it should appear in records" : "Student's full name as it should appear in records"}</p>
                  </div>
                  <div className="mb-4">
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                      Email <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="email"
                      aria-describedby="email-help"
                      id="email"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      value={newStudent.email}
                      onChange={(e) => setNewStudent({ ...newStudent, email: e.target.value })}
                      required
                    />
                  </div>
                  <div className="mb-4">
                    <p id="email-help" className="mt-1 text-xs text-gray-500">{isConsultant ? "Client's email address for communication" : "Student's email address for communication"}</p>
                  </div>
                  <div className="mb-4">
                    <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
                      Phone Number
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      aria-describedby="phone-help"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      value={newStudent.phone}
                      onChange={(e) => setNewStudent({ ...newStudent, phone: e.target.value })}
                      required
                    />
                  </div>
                  <div className="mb-4">
                    <label htmlFor="gender" className="block text-sm font-medium text-gray-700 mb-1">
                      Gender
                    </label>
                    <select
                      id="gender"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      value={newStudent.gender}
                      onChange={(e) => setNewStudent({ ...newStudent, gender: e.target.value })}
                      required
                    >
                      <option value="male">Male</option>
                      <option value="female">Female</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                  <div className="mb-4">
                    <p id="phone-help" className="mt-1 text-xs text-gray-500">Contact number (optional)</p>
                  </div>
                  <div className="mb-4">
                    <label htmlFor="program" className="block text-sm font-medium text-gray-700 mb-1">
                      Assign to Program <span className="text-red-500">*</span>
                    </label>
                    <select
                      id="program"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      aria-describedby="program-help"
                      value={newStudent.program}
                      onChange={(e) => setNewStudent({ ...newStudent, program: e.target.value })}
                    >
                      <option value="">Select a program</option>
                      {programs.map((prog) => (
                        <option key={prog._id} value={prog._id}>
                          {prog.name}
                        </option>
                      ))}
                    </select>
                    <p id="program-help" className="mt-1 text-xs text-gray-500">Select the program this student is enrolling in</p>
                  </div>
                  <div className="mt-6 flex justify-end space-x-3">
                    <button
                      type="button"
                      onClick={() => setShowAddModal(false)}
                      className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={isAddingStudent}
                      className={`px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 ${isAddingStudent
                        ? 'bg-blue-400 cursor-not-allowed'
                        : 'bg-blue-600 hover:bg-blue-700'
                        }`}
                    >
                      {isAddingStudent ? (
                        <div className="flex items-center">
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                          Adding Student...
                        </div>
                      ) : (
                        isConsultant ? 'Add Client' : 'Add Student'
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}

        {/* Bulk Import Modal */}
        {showBulkImportModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-medium text-gray-900">Bulk Import Students</h3>
                  <button
                    onClick={() => setShowBulkImportModal(false)}
                    className="text-gray-400 hover:text-gray-500"
                  >
                    &times;
                  </button>
                </div>
                <div className="mb-4 p-4 bg-blue-50 rounded-lg">
                  <h4 className="font-medium text-blue-800 mb-2">CSV Format Instructions:</h4>
                  <p className="text-sm text-blue-700 mb-2">Your CSV should have the following columns (in any order):</p>
                  <ul className="text-sm text-blue-700 list-disc pl-5">
                    <li><span className="font-mono">name</span> (Student's full name)</li>
                    <li><span className="font-mono">email</span> (Student's email address)</li>
                    <li><span className="font-mono">phone</span> (Student's phone number)</li>
                    <li><span className="font-mono">program</span> (Exact name of the program)</li>
                  </ul>
                  <p className="text-sm text-blue-700 mt-2">{isConsultant ? 'All clients will be imported as active.' : 'All students will be imported as active.'}</p>
                </div>
                <form onSubmit={handleBulkImport}>
                  <div className="mb-4">
                    <label htmlFor="csvData" className="block text-sm font-medium text-gray-700 mb-1">
                      Paste CSV Data
                    </label>
                    <textarea
                      id="csvData"
                      rows={10}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 font-mono text-sm"
                      placeholder="name,email,phone,program..."
                      value={csvData}
                      onChange={(e) => setCsvData(e.target.value)}
                      required
                    />
                  </div>
                  <div className="mt-6 flex justify-end space-x-3">
                    <button
                      type="button"
                      onClick={() => setShowBulkImportModal(false)}
                      className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    >
                      Import Students
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}

        {/* Send Message Modal */}
        {showMessageModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
              <div className="p-6">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-medium text-gray-900">
                    Send Message to {students.find((s) => s.studentId === messageStudentId)?.name || 'Student'}
                  </h3>
                  <button
                    onClick={() => {
                      setShowMessageModal(false);
                      setMessageStudentId(null);
                      setMessageContent('');
                    }}
                    className="text-gray-400 hover:text-gray-500"
                  >
                    &times;
                  </button>
                </div>
                <form onSubmit={async (e) => {
                  e.preventDefault();
                  if (!messageContent.trim()) {
                    toast.error('Please enter a message');
                    return;
                  }
                  setIsSendingMessage(true);
                  try {
                    const targetStudent = students.find((s) => s.studentId === messageStudentId);
                    if (!targetStudent) {
                      toast.error('Student not found');
                      return;
                    }

                    await HttpInterceptor.post('/api/messages/trainer/send', {
                      studentId: messageStudentId,
                      content: messageContent.trim(),
                      type: 'message',
                      programId: targetStudent.programId || null
                    });

                    const studentName = targetStudent?.name || 'Student';
                    toast.success(`✓ Message sent to ${studentName}`);
                    setShowMessageModal(false);
                    setMessageStudentId(null);
                    setMessageContent('');
                  } catch (err) {
                    const friendlyError = getContextualError('send message', err);
                    toast.error(friendlyError);
                    console.error('Send message error:', err);
                  } finally {
                    setIsSendingMessage(false);
                  }
                }}>
                  <div className="mb-4">
                    <label htmlFor="messageContent" className="block text-sm font-medium text-gray-700 mb-1">
                      Message
                    </label>
                    <textarea
                      id="messageContent"
                      rows={4}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Type your message here..."
                      value={messageContent}
                      onChange={(e) => setMessageContent(e.target.value)}
                      required
                    />
                  </div>
                  <div className="mt-6 flex justify-end space-x-3">
                    <button
                      type="button"
                      onClick={() => {
                        setShowMessageModal(false);
                        setMessageStudentId(null);
                        setMessageContent('');
                      }}
                      className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={isSendingMessage}
                      className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isSendingMessage ? 'Sending...' : 'Send Message'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}

        {/* Attendance Modal */}
        {showAttendanceModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-medium text-gray-900">Class Attendance</h3>
                  <button
                    onClick={() => {
                      setShowAttendanceModal(null);
                      setClassAttendance([]);
                    }}
                    className="text-gray-400 hover:text-gray-500"
                  >
                    &times;
                  </button>
                </div>
                {classAttendance.length > 0 ? (
                  <div className="space-y-2">
                    {classAttendance.map((record, index) => (
                      <div key={index} className="border border-gray-200 rounded-lg p-3">
                        <p className="text-sm font-medium">{formatDate(record.sessionDate)}</p>
                        <span className={`inline-block mt-1 px-2 py-1 text-xs rounded-full ${record.status === 'attended' ? 'bg-green-100 text-green-800' :
                          record.status === 'excused' ? 'bg-yellow-100 text-yellow-800' :
                            'bg-red-100 text-red-800'
                          }`}>
                          {record.status}
                        </span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-center text-gray-500 py-8">No attendance records found</p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Invite Student Modal */}
        {showInviteModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-medium text-gray-900">Invite Student</h3>
                  <button
                    onClick={() => {
                      setShowInviteModal(false);
                      setInviteData({
                        contact: '',
                        contactType: 'email',
                        programId: '',
                        message: ''
                      });
                    }}
                    className="text-gray-400 hover:text-gray-500"
                  >
                    &times;
                  </button>
                </div>
                <form onSubmit={handleInviteStudent}>
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Invite Via
                    </label>
                    <div className="flex gap-4">
                      <label className="flex items-center">
                        <input
                          type="radio"
                          name="contactType"
                          value="email"
                          checked={inviteData.contactType === 'email'}
                          onChange={(e) => setInviteData({ ...inviteData, contactType: e.target.value })}
                          className="mr-2"
                        />
                        <FaEnvelope className="mr-1 text-gray-600" />
                        <span className="text-sm">Email</span>
                      </label>
                      <label className="flex items-center">
                        <input
                          type="radio"
                          name="contactType"
                          value="whatsapp"
                          checked={inviteData.contactType === 'whatsapp'}
                          onChange={(e) => setInviteData({ ...inviteData, contactType: e.target.value })}
                          className="mr-2"
                        />
                        <FaPhone className="mr-1 text-green-600" />
                        <span className="text-sm">WhatsApp</span>
                      </label>
                    </div>
                  </div>
                  <div className="mb-4">
                    <label htmlFor="inviteContact" className="block text-sm font-medium text-gray-700 mb-1">
                      {inviteData.contactType === 'email' ? 'Email Address' : 'WhatsApp Number'}
                    </label>
                    <input
                      type={inviteData.contactType === 'email' ? 'email' : 'tel'}
                      id="inviteContact"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      placeholder={inviteData.contactType === 'email' ? 'student@example.com' : '+91 9876543210'}
                      value={inviteData.contact}
                      onChange={(e) => setInviteData({ ...inviteData, contact: e.target.value })}
                      required
                    />
                    {inviteData.contactType === 'whatsapp' && (
                      <p className="mt-1 text-xs text-gray-500">
                        Include country code (e.g., +91 for India)
                      </p>
                    )}
                  </div>
                  <div className="mb-4">
                    <label htmlFor="inviteProgram" className="block text-sm font-medium text-gray-700 mb-1">
                      Select Program
                    </label>
                    <select
                      id="inviteProgram"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      value={inviteData.programId}
                      onChange={(e) => setInviteData({ ...inviteData, programId: e.target.value })}
                      required
                    >
                      <option value="">Select a program</option>
                      {programs.filter(p => p.isActive).map((prog) => (
                        <option key={prog._id} value={prog._id}>
                          {prog.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="mb-4">
                    <label htmlFor="inviteMessage" className="block text-sm font-medium text-gray-700 mb-1">
                      Message (Optional)
                    </label>
                    <textarea
                      id="inviteMessage"
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Add a personal message to your invitation..."
                      value={inviteData.message}
                      onChange={(e) => setInviteData({ ...inviteData, message: e.target.value })}
                    />
                  </div>
                  <div className="flex justify-end space-x-3">
                    <button
                      type="button"
                      onClick={() => {
                        setShowInviteModal(false);
                        setInviteData({
                          contact: '',
                          contactType: 'email',
                          programId: '',
                          message: ''
                        });
                      }}
                      className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={isInviting}
                      className="px-4 py-2 bg-green-600 text-white rounded-md shadow-sm text-sm font-medium hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                    >
                      {isInviting ? (
                        <>
                          <span className="animate-spin">⏳</span>
                          Sending...
                        </>
                      ) : (
                        <>
                          {inviteData.contactType === 'email' ? (
                            <>
                              <FaEnvelope />
                              Send Email
                            </>
                          ) : (
                            <>
                              <FaPhone />
                              Generate WhatsApp Link
                            </>
                          )}
                        </>
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}

        {/* Confirmation Modal */}
        {showDeleteModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <div className="flex justify-between items-center mb-4">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <FaTrash className="h-6 w-6 text-red-600" />
                    </div>
                    <div className="ml-3">
                      <h3 className="text-lg font-medium text-gray-900">Confirm Deletion</h3>
                    </div>
                  </div>
                  <button
                    onClick={cancelDelete}
                    className="text-gray-400 hover:text-gray-500"
                  >
                    &times;
                  </button>
                </div>
                <div className="mb-6">
                  <p className="text-sm text-gray-600">
                    Are you sure you want to {deleteItem?.type === 'reject' ? 'reject and remove' : 'remove'} <span className="font-medium">{deleteItem?.name}</span>?
                    This action cannot be undone.
                  </p>
                </div>
                <div className="flex justify-end space-x-3">
                  <button
                    onClick={cancelDelete}
                    className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={confirmDelete}
                    className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                    disabled={isLoading}
                  >
                    {isLoading ? (deleteItem?.type === 'reject' ? 'Rejecting...' : 'Deleting...') : (deleteItem?.type === 'reject' ? 'Reject' : 'Delete')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Payment History Modal */}
        {showPaymentHistory && (
          <PaymentHistoryModal
            isOpen={!!showPaymentHistory}
            onClose={() => setShowPaymentHistory(null)}
            studentId={students.find(s => s.planId === showPaymentHistory)?._id}
            studentName={students.find(s => s.planId === showPaymentHistory)?.name}
          />
        )}

        {/* Payment Plan Assignment Modal */}
        {showPlanAssignment && (() => {
          const student = students.find(s => s.planId === showPlanAssignment);
          return student ? (
            <PaymentPlanAssignmentModal
              isOpen={!!showPlanAssignment}
              onClose={() => setShowPlanAssignment(null)}
              studentId={student.studentId}
              studentName={student.name}
              programId={student.programDetails?.programId || student.programId}
              currentPlanId={student.paymentPlanId}
              totalAmount={student.payment?.totalAmount || 0}
              onPlanAssigned={() => {
                getStudents();
              }}
            />
          ) : null;
        })()}

        {/* Progress Breakdown Modal */}
        {showProgressBreakdown && (() => {
          const student = students.find(s => s.planId === showProgressBreakdown);
          return student ? (
            <ProgressBreakdownModal
              isOpen={!!showProgressBreakdown}
              onClose={() => setShowProgressBreakdown(null)}
              studentId={student.studentId}
              studentName={student.name}
              studentStatus={student.status}
              isConsultant={isConsultant}
            />
          ) : null;
        })()}

        {/* Attendance Calendar Modal */}
        {showAttendanceCalendar && (() => {
          const student = students.find(s => s.planId === showAttendanceCalendar);
          return student ? (
            <AttendanceCalendarModal
              isOpen={!!showAttendanceCalendar}
              onClose={() => setShowAttendanceCalendar(null)}
              studentId={student.studentId}
              studentName={student.name}
              studentStatus={student.status}
              isConsultant={isConsultant}
            />
          ) : null;
        })()}

        {/* Communication History Modal */}
        {showCommunicationHistory && (
          <CommunicationHistoryModal
            isOpen={!!showCommunicationHistory}
            onClose={() => setShowCommunicationHistory(null)}
            studentId={showCommunicationHistory}
            studentName={students.find(s => s._id === showCommunicationHistory)?.name || 'Student'}
          />
        )}
      </div>
    </>
  );
}

export default MyStudents;